/**
 * ConductSession -- the provider's in-session experience.
 *
 * Route: /sessions/:id/conduct
 *
 * States:
 *   1. Loading    -- fetching session metadata
 *   2. NoSession  -- session row not yet provisioned; polls until it appears
 *   3. Lobby      -- pre-join: device check, modality selector, participant list
 *   4. Notice     -- recording-notice audio overlay (blocks until complete)
 *   5. InSession  -- active Twilio Video SDK connection (or phone-connected state)
 *   6. Ended      -- post-session summary with duration, link back
 *
 * Consumes the private GraphQL API:
 *   Queries:  sessionStatus, transcriptEvents
 *   Mutations: generateVideoToken, triggerCallMeProvider,
 *              logNoticePlayed, saveSessionNotes, saveSessionActionItems,
 *              saveTranscriptEvents
 */

import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { Link, useParams } from "react-router";
import { useQuery, useMutation } from "@apollo/client";
import {
  ChevronLeft,
  Mic,
  MicOff,
  Video as VideoIcon,
  VideoOff,
  Phone,
  PhoneOff,
  PhoneCall,
  PhoneOutgoing,
  Users,
  FileText,
  Plus,
  Check,
  X,
  AlertCircle,
  Loader2,
  Send,
  Maximize2,
  Minimize2,
  Disc,
} from "lucide-react";
import { Avatar, Badge, SegmentedControl, clientInitials, clientDisplayName } from "../components/ui";
import { useToastPush } from "../components/ui/Toast";
import {
  GET_SESSION_STATUS,
  GENERATE_VIDEO_TOKEN,
  TRIGGER_CALL_ME_PROVIDER,
  END_SESSION_MEETING,
  SET_SESSION_RECORDING,
  SAVE_SESSION_NOTES,
  SAVE_SESSION_ACTION_ITEMS,
  LOG_NOTICE_PLAYED,
  SAVE_TRANSCRIPT_EVENTS,
  SEND_SESSION_LINK,
} from "../apollo/conductSession";
import {
  GET_SESSION_DETAIL,
  GET_BOOKING_FILES,
  GET_PRIMARY_CUSTOMER,
  GET_BOOKING_CUSTOMERS,
  GET_PROVIDERS,
} from "../apollo/sessions";
import {
  formatTimeRange,
  formatDuration,
  formatDateLabel,
  methodToLocation,
  formatFileSize,
} from "../utils/sessionTime";

// ── Types ───────────────────────────────────────────────────────────

type PageState = "loading" | "no-session" | "lobby" | "notice" | "in-session" | "ended";
type Modality = "video" | "phone";
type NotesTab = "notes" | "actions" | "transcript";

interface AccessCode {
  id: string;
  code: string;
  participantRole: string;
  participantId: string;
  expiresAt: string;
}

interface SessionEvent {
  id: string;
  eventType: string;
  participantId: string;
  participantRole: string;
  participantName: string;
  joinMethod: string;
  occurredAt: string;
}

interface ActionItem {
  id: string;
  text: string;
  isCompleted: boolean;
  sortOrder: number;
}

interface TranscriptUtterance {
  eventId: string;
  participantIdentity: string;
  text: string;
  timestamp: number;
  sequence: number;
}

interface ProviderRow {
  id: string;
  firstName: string;
  lastName: string;
}

// The Twilio SDK's EventEmitter typing is loose — `on(event, handler)` where
// handler is `(...args: any[]) => void`. Mirror that with `any[]` here so
// strongly-typed handlers (e.g. `(p: TwilioParticipant) => void`) are
// assignable. `unknown[]` would force every handler to accept unknown args
// and breaks call-site ergonomics for no real safety win.
// eslint-disable-next-line @typescript-eslint/no-explicit-any
type TwilioEventHandler = (...args: any[]) => void;

interface TwilioRoom {
  disconnect: () => void;
  on: (event: string, handler: TwilioEventHandler) => void;
  off: (event: string, handler: TwilioEventHandler) => void;
  participants: Map<string, TwilioParticipant>;
  localParticipant: TwilioLocalParticipant;
  name: string;
  state: string;
  dominantSpeaker?: TwilioParticipant;
}

interface TwilioParticipant {
  identity: string;
  sid: string;
  tracks: Map<string, TwilioTrackPublication>;
  on: (event: string, handler: TwilioEventHandler) => void;
  off: (event: string, handler: TwilioEventHandler) => void;
}

interface TwilioLocalParticipant extends TwilioParticipant {
  audioTracks: Map<string, TwilioTrackPublication>;
  videoTracks: Map<string, TwilioTrackPublication>;
}

interface TwilioTrackPublication {
  track: TwilioTrack | null;
  trackName: string;
  kind: "audio" | "video" | "data";
  isTrackEnabled: boolean;
  on: (event: string, handler: TwilioEventHandler) => void;
}

interface TwilioTrack {
  kind: "audio" | "video";
  attach: (el?: HTMLMediaElement) => HTMLMediaElement;
  detach: (el?: HTMLMediaElement) => HTMLMediaElement[];
  isEnabled: boolean;
  enable: () => void;
  disable: () => void;
  stop: () => void;
}

// ── Helpers ─────────────────────────────────────────────────────────

function formatElapsed(seconds: number): string {
  const h = Math.floor(seconds / 3600);
  const m = Math.floor((seconds % 3600) / 60);
  const s = seconds % 60;
  const pad = (n: number) => String(n).padStart(2, "0");
  return h > 0 ? `${pad(h)}:${pad(m)}:${pad(s)}` : `${pad(m)}:${pad(s)}`;
}

interface CustomerRow {
  id: string;
  name: string;
  alias: string | null;
  email?: string | null;
  phoneNumber?: string | null;
}

function identityToLabel(
  identity: string,
  customerByIdentity: Map<string, CustomerRow>,
): string {
  // Identities are like "provider-uuid" or "customer-uuid"
  // Accept both 'provider-' and 'lawyer-' during one deploy cycle; drop 'lawyer-' next cycle
  if (identity.startsWith("provider-") || identity.startsWith("lawyer-")) return "You";
  const customer = customerByIdentity.get(identity);
  if (customer) return clientDisplayName(customer);
  // Fallback for unknown identities: show a short, human-readable hint
  // rather than dumping the full UUID on the user.
  return identity.startsWith("customer-")
    ? `Client ${identity.slice(9, 13)}`
    : identity;
}

// ── VideoTile ───────────────────────────────────────────────────────
// Owns its own <video> element. The Twilio track is attached in a
// useEffect so React owns the DOM and we never race with state updates
// the way an imperative ref + appendChild/innerHTML pattern does. This
// is the pattern used by twilio/twilio-video-app-react.
//
// When the underlying track is disabled (track.disable() locally, or
// a remote participant turns their camera off) the <video> element
// would otherwise freeze on the last decoded frame. We listen for the
// track's 'enabled'/'disabled' events and render a placeholder tile
// instead so the user sees a clear "camera off" state.
function VideoTile({
  track,
  mirror,
  muted,
  className,
}: {
  track: TwilioTrack;
  mirror?: boolean;
  muted?: boolean;
  className?: string;
}) {
  const videoRef = useRef<HTMLVideoElement | null>(null);
  const [isEnabled, setIsEnabled] = useState<boolean>(track.isEnabled !== false);

  useEffect(() => {
    setIsEnabled(track.isEnabled !== false);
    const handleEnabled = () => setIsEnabled(true);
    const handleDisabled = () => setIsEnabled(false);
    // TwilioTrack extends EventEmitter; on/off exist at runtime even if
    // not in our minimal interface.
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    const t = track as any;
    t.on?.("enabled", handleEnabled);
    t.on?.("disabled", handleDisabled);
    return () => {
      t.off?.("enabled", handleEnabled);
      t.off?.("disabled", handleDisabled);
    };
  }, [track]);

  useEffect(() => {
    const el = videoRef.current;
    if (!el || !isEnabled) return;
    track.attach(el);
    // Firefox blocks unmuted autoplay even after a user gesture; the
    // autoplay/muted attributes on the element plus an explicit play()
    // catch any straggling cases.
    el.play().catch(() => {});
    return () => {
      track.detach(el);
    };
  }, [track, isEnabled]);

  if (!isEnabled) {
    return (
      <div
        className={className}
        style={{
          width: "100%",
          height: "100%",
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          background: "#222",
          color: "rgba(255, 255, 255, 0.55)",
        }}
      >
        <VideoOff size={32} />
      </div>
    );
  }

  return (
    <video
      ref={videoRef}
      autoPlay
      playsInline
      muted={muted}
      className={className}
      style={{
        width: "100%",
        height: "100%",
        objectFit: "cover",
        transform: mirror ? "scaleX(-1)" : undefined,
      }}
    />
  );
}

// ── Component ───────────────────────────────────────────────────────

export function ConductSession() {
  const { id: bookingId } = useParams();
  const toast = useToastPush();

  // ── Core data ───────────────────────────────────────────────────
  const {
    data: sessionData,
    loading: sessionLoading,
    refetch: refetchSession,
    startPolling: startSessionPolling,
  } = useQuery(GET_SESSION_STATUS, {
    variables: { bookingId },
    skip: !bookingId,
    pollInterval: 10000,
  });
  const { data: bookingData, loading: bookingLoading } = useQuery(GET_SESSION_DETAIL, {
    variables: { id: bookingId, includeDeleted: false },
    skip: !bookingId,
  });
  const { data: filesData } = useQuery(GET_BOOKING_FILES, {
    variables: { filter: { bookingId }, sortField: "created_at", sortOrder: "DESC" },
    skip: !bookingId,
  });
  const { data: customerData } = useQuery(GET_PRIMARY_CUSTOMER, {
    variables: { filter: { bookingId, isPrimary: true } },
    skip: !bookingId,
  });
  // All customers on the booking — used to label per-participant tiles and
  // the sidebar roster. GET_PRIMARY_CUSTOMER only returns the lead client.
  const { data: allCustomersData } = useQuery(GET_BOOKING_CUSTOMERS, {
    variables: { filter: { bookingId } },
    skip: !bookingId,
  });
  const { data: providerData } = useQuery(GET_PROVIDERS, {
    variables: { perPage: 1000, sortField: "lastName", sortOrder: "ASC" },
  });

  // ── Mutations ─────────────────────────────────────────────────────
  const [generateToken] = useMutation(GENERATE_VIDEO_TOKEN);
  const [triggerCallMeProvider] = useMutation(TRIGGER_CALL_ME_PROVIDER);
  const [endSessionMeeting] = useMutation(END_SESSION_MEETING);
  const [setSessionRecording] = useMutation(SET_SESSION_RECORDING);
  const [saveNotesMutation] = useMutation(SAVE_SESSION_NOTES);
  const [saveActionItemsMutation] = useMutation(SAVE_SESSION_ACTION_ITEMS);
  const [logNoticePlayed] = useMutation(LOG_NOTICE_PLAYED);
  const [saveTranscriptEvents] = useMutation(SAVE_TRANSCRIPT_EVENTS);
  const [sendSessionLink, { loading: sendingLink }] = useMutation(SEND_SESSION_LINK);
  const [linkJustSent, setLinkJustSent] = useState(false);

  // ── Local state ───────────────────────────────────────────────────
  const [pageState, setPageState] = useState<PageState>("loading");
  const [modality, setModality] = useState<Modality>("video");
  // End-call choice menu: "Leave call" (provider leaves, room stays live)
  // vs "End meeting" (room completed server-side for everyone).
  const [endMenuOpen, setEndMenuOpen] = useState(false);
  const [isEndingMeeting, setIsEndingMeeting] = useState(false);
  // True once the meeting was ended for everyone — the room is completed
  // server-side, so the ended screen drops Rejoin and leads with Close.
  const [endedForEveryone, setEndedForEveryone] = useState(false);
  // Maximized: the video area takes over the whole browser tab (rails,
  // notes, and footer hide; the header stays for controls/end-call).
  const [isMaximized, setIsMaximized] = useState(false);
  const [notesTab, setNotesTab] = useState<NotesTab>("notes");

  // Video state
  const [isMuted, setIsMuted] = useState(false);
  const [isCameraOff, setIsCameraOff] = useState(false);
  const [isTogglingRecording, setIsTogglingRecording] = useState(false);
  const [isJoining, setIsJoining] = useState(false);
  const [isCallingMe, setIsCallingMe] = useState(false);
  const [isPhoneConnected, setIsPhoneConnected] = useState(false);
  const roomRef = useRef<TwilioRoom | null>(null);
  const localTracksRef = useRef<TwilioTrack[]>([]);
  // Tracks rendered by VideoTile. Using state (not refs) lets React mount
  // the <video> element after the in-session DOM commits, eliminating the
  // race the old imperative attach pattern hit on the lobby → in-session
  // transition.
  const [localVideoTrack, setLocalVideoTrack] = useState<TwilioTrack | null>(null);
  // Remote video tracks keyed by participant identity. Using a Map allows us
  // to track multiple participants in a grid/gallery layout.
  const [remoteVideoTracks, setRemoteVideoTracks] = useState<Map<string, TwilioTrack>>(new Map());
  // Connected remote participant identities. Decoupled from remoteVideoTracks
  // so participants who joined audio-only or with camera off still appear in
  // the gallery as a labeled placeholder tile rather than disappearing.
  const [remoteIdentities, setRemoteIdentities] = useState<Set<string>>(new Set());
  // Track the dominant speaker for layout prioritization
  const [dominantSpeaker, setDominantSpeaker] = useState<string | undefined>(undefined);
  // Holds the room.disconnect bound to a beforeunload listener so we can
  // remove the same function reference on graceful disconnect.
  const beforeUnloadHandlerRef = useRef<(() => void) | null>(null);

  // Timer: derived from server-provided elapsed time, with local accumulator for current session
  const serverElapsedSeconds = sessionData?.sessionStatus?.elapsedSecondsBillable ?? 0;
  const timerPaused = sessionData?.sessionStatus?.timerPaused ?? false;
  // localAccumulator is inter-poll smoothing ONLY: the server value already
  // includes live running time (now - last_resumed_at), so every poll
  // response resets the accumulator. Without the reset the display summed
  // both sources and ran at ~2x real time.
  const [localAccumulator, setLocalAccumulator] = useState(0);
  const timerRef = useRef<ReturnType<typeof setInterval> | null>(null);
  useEffect(() => {
    setLocalAccumulator(0);
  }, [serverElapsedSeconds]);

  // Notes
  const [notesContent, setNotesContent] = useState("");
  const [notesSaveStatus, setNotesSaveStatus] = useState<"idle" | "saving" | "saved">("idle");
  const notesSaveTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  // Holds the latest debounced-but-not-yet-saved notes content so unmount
  // cleanup can fire-and-forget the last keystrokes.
  const pendingNotesContentRef = useRef<string | null>(null);

  // Action items
  const [actionItems, setActionItems] = useState<ActionItem[]>([]);
  const [newItemText, setNewItemText] = useState("");
  const actionSaveTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const pendingActionItemsRef = useRef<ActionItem[] | null>(null);

  // Transcript (live from Twilio SDK)
  const [transcriptEntries, setTranscriptEntries] = useState<TranscriptUtterance[]>([]);
  const transcriptBufferRef = useRef<TranscriptUtterance[]>([]);
  const transcriptSeqRef = useRef(0);
  const transcriptFlushTimerRef = useRef<ReturnType<typeof setInterval> | null>(null);

  // Notice audio
  const [noticeError, setNoticeError] = useState(false);

  // Rejoin flag — set true when the provider clicks Rejoin from the ended
  // screen. Used to suppress the auto-transition useEffect that would
  // otherwise immediately bounce them back to "ended" because session.endedAt
  // is still stamped from the disconnect they just made. Cleared by
  // handleEndCall so a subsequent end (manual or otherwise) transitions
  // normally.
  const rejoinedRef = useRef(false);

  // ── Derived data ────────────────────────────────────────────────
  const session = sessionData?.sessionStatus?.session;
  const recordingOn = session?.recordingEnabled === true;
  const recordingAvailable = sessionData?.sessionStatus?.recordingAvailable === true;
  const accessCodes: AccessCode[] = sessionData?.sessionStatus?.accessCodes ?? [];
  const events: SessionEvent[] = sessionData?.sessionStatus?.events ?? [];
  const booking = bookingData?.Booking;
  const files = filesData?.allBookingFiles ?? [];
  const primaryCustomer = customerData?.allPeople?.[0] ?? null;
  const allProviders = providerData?.allProviders ?? [];
  const providerMap = new Map<string, ProviderRow>(
    allProviders.map((l: ProviderRow) => [l.id, l]),
  );
  const avail = booking?.availability;
  const provider: ProviderRow | undefined = avail ? providerMap.get(avail.providerId) : undefined;
  const providerName = provider ? `${provider.firstName} ${provider.lastName}` : null;
  const providerInitials = provider ? `${provider.firstName[0]}${provider.lastName[0]}` : "??";

  const customerInitials = primaryCustomer
    ? clientInitials(primaryCustomer)
    : "??";

  // Per-participant customer lookups so multi-customer bookings show each
  // person's correct name + initials in the roster, tiles, and labels —
  // not just the primary customer copied across every row.
  const allCustomers: CustomerRow[] = allCustomersData?.allPeople ?? [];
  const customerByParticipantId = useMemo(() => {
    const m = new Map<string, CustomerRow>();
    for (const c of allCustomers) m.set(c.id, c);
    return m;
  }, [allCustomers]);
  const customerByIdentity = useMemo(() => {
    const m = new Map<string, CustomerRow>();
    for (const c of allCustomers) m.set(`customer-${c.id}`, c);
    return m;
  }, [allCustomers]);


  // Find provider and customer access codes
  // Accept both 'provider' and 'lawyer' during one deploy cycle; drop 'lawyer' next cycle
  const providerCode = accessCodes.find((c) => c.participantRole === "provider" || c.participantRole === "lawyer");
  const customerCodes = accessCodes.filter((c) => c.participantRole === "customer");

  // Tenant phone number from session status (resolved from tenant.twilio_phone_number)
  const tenantPhoneNumber = sessionData?.sessionStatus?.tenantPhoneNumber ?? null;

  // Recording notice audio URL from session status (resolved from config.recording_notice_url)
  const recordingNoticeUrl = sessionData?.sessionStatus?.recordingNoticeUrl ?? "";

  // Computed elapsed seconds: server-provided base + local accumulator for current session
  const elapsedSeconds = serverElapsedSeconds + localAccumulator;

  // ── Initialize page state from data ───────────────────────────────
  useEffect(() => {
    if (sessionLoading || bookingLoading) return;

    if (!session) {
      setPageState("no-session");
      return;
    }

    if (session.status === "completed" || session.endedAt) {
      // The provider may have clicked Rejoin from the ended screen — in which
      // case session.endedAt is stale relative to their intent. Skip the
      // auto-transition. They'll see "ended" again only if handleEndCall
      // runs (which clears the flag).
      if (rejoinedRef.current) return;
      setPageState("ended");
      return;
    }

    if (pageState === "loading" || pageState === "no-session") {
      // Set default modality from booking method
      if (booking?.method === "phone") {
        setModality("phone");
      } else {
        setModality("video");
      }
      setPageState("lobby");
    }
  }, [sessionLoading, bookingLoading, session, booking, pageState]);

  // ── Initialize notes/action items from server data — ONCE ─────────
  // After the first hydration (or the first local edit) local state is
  // authoritative; the 10s poll must never overwrite it. The previous
  // empty-means-rehydrate guards made cleared notes and unticked/removed
  // action items reappear on the next poll.
  const notesHydratedRef = useRef(false);
  useEffect(() => {
    if (notesHydratedRef.current) return;
    const serverNotes = sessionData?.sessionStatus?.notes;
    if (serverNotes && serverNotes.length > 0) {
      notesHydratedRef.current = true;
      setNotesContent(serverNotes[0].content ?? "");
    }
  }, [sessionData?.sessionStatus?.notes]);

  const actionsHydratedRef = useRef(false);
  useEffect(() => {
    if (actionsHydratedRef.current) return;
    const serverItems = sessionData?.sessionStatus?.actionItems;
    if (serverItems && serverItems.length > 0) {
      actionsHydratedRef.current = true;
      setActionItems(
        serverItems.map((item: ActionItem) => ({
          id: item.id,
          text: item.text,
          isCompleted: item.isCompleted,
          sortOrder: item.sortOrder,
        })),
      );
    }
  }, [sessionData?.sessionStatus?.actionItems]);

  // ── Timer ─────────────────────────────────────────────────────────
  useEffect(() => {
    if (pageState === "in-session") {
      timerRef.current = setInterval(() => {
        // Only increment if timer is not paused (waiting for customer)
        if (!timerPaused) {
          setLocalAccumulator((prev) => prev + 1);
        }
      }, 1000);
    }
    return () => {
      if (timerRef.current) clearInterval(timerRef.current);
    };
  }, [pageState, timerPaused]);

  // Leave maximize when we leave the in-session screen.
  useEffect(() => {
    if (pageState !== "in-session") setIsMaximized(false);
  }, [pageState]);

  // ── Navigation warning ────────────────────────────────────────────
  useEffect(() => {
    if (pageState !== "in-session") return;
    const handler = (e: BeforeUnloadEvent) => {
      e.preventDefault();
    };
    window.addEventListener("beforeunload", handler);
    return () => window.removeEventListener("beforeunload", handler);
  }, [pageState]);

  // ── Debounced notes save ──────────────────────────────────────────
  const saveNotes = useCallback(
    (content: string) => {
      if (!bookingId) return;
      setNotesSaveStatus("saving");
      saveNotesMutation({ variables: { bookingId, content } })
        .then(() => {
          pendingNotesContentRef.current = null;
          setNotesSaveStatus("saved");
          setTimeout(() => setNotesSaveStatus("idle"), 2000);
        })
        .catch(() => {
          setNotesSaveStatus("idle");
          toast({ kind: "error", title: "Failed to save notes" });
        });
    },
    [bookingId, saveNotesMutation, toast],
  );

  const handleNotesChange = useCallback(
    (value: string) => {
      notesHydratedRef.current = true;
      setNotesContent(value);
      pendingNotesContentRef.current = value;
      if (notesSaveTimerRef.current) clearTimeout(notesSaveTimerRef.current);
      notesSaveTimerRef.current = setTimeout(() => saveNotes(value), 2000);
    },
    [saveNotes],
  );

  // ── Save action items ─────────────────────────────────────────────
  const saveActionItems = useCallback(
    (items: ActionItem[]) => {
      if (!bookingId) return;
      saveActionItemsMutation({
        variables: {
          bookingId,
          items: items.map((item, idx) => ({
            id: item.id,
            text: item.text,
            isCompleted: item.isCompleted,
            sortOrder: idx,
          })),
        },
      })
        .then((result) => {
          // Only clear the pending marker if no NEWER edits were queued
          // while this save was in flight.
          if (pendingActionItemsRef.current === items) {
            pendingActionItemsRef.current = null;
          }
          // Reconcile temp ids -> server ids WITHOUT overwriting local
          // state: the server echo reflects what we SENT, not edits the
          // user made while the request was in flight (a wholesale
          // setActionItems here visibly reverted checkbox toggles). The
          // response preserves order, so map ids by index of the sent
          // batch.
          const saved = result.data?.saveSessionActionItems;
          if (saved && saved.length === items.length) {
            const idMap = new Map<string, string>();
            items.forEach((sent, idx) => {
              if (sent.id !== saved[idx].id) idMap.set(sent.id, saved[idx].id);
            });
            if (idMap.size > 0) {
              setActionItems((current) =>
                current.map((it) =>
                  idMap.has(it.id) ? { ...it, id: idMap.get(it.id)! } : it,
                ),
              );
            }
          }
        })
        .catch(() => {
          toast({ kind: "error", title: "Failed to save action items" });
        });
    },
    [bookingId, saveActionItemsMutation, toast],
  );

  const debouncedSaveActions = useCallback(
    (items: ActionItem[]) => {
      pendingActionItemsRef.current = items;
      if (actionSaveTimerRef.current) clearTimeout(actionSaveTimerRef.current);
      actionSaveTimerRef.current = setTimeout(() => saveActionItems(items), 1500);
    },
    [saveActionItems],
  );

  const addActionItem = useCallback(() => {
    if (!newItemText.trim()) return;
    actionsHydratedRef.current = true;
    const updated = [
      ...actionItems,
      {
        id: `temp-${Date.now()}`,
        text: newItemText.trim(),
        isCompleted: false,
        sortOrder: actionItems.length,
      },
    ];
    setActionItems(updated);
    setNewItemText("");
    debouncedSaveActions(updated);
  }, [newItemText, actionItems, debouncedSaveActions]);

  const toggleActionItem = useCallback(
    (itemId: string) => {
      actionsHydratedRef.current = true;
      const updated = actionItems.map((item) =>
        item.id === itemId ? { ...item, isCompleted: !item.isCompleted } : item,
      );
      setActionItems(updated);
      debouncedSaveActions(updated);
    },
    [actionItems, debouncedSaveActions],
  );

  const removeActionItem = useCallback(
    (itemId: string) => {
      actionsHydratedRef.current = true;
      const updated = actionItems.filter((item) => item.id !== itemId);
      setActionItems(updated);
      debouncedSaveActions(updated);
    },
    [actionItems, debouncedSaveActions],
  );

  // ── Video join flow ───────────────────────────────────────────────
  const handleJoinVideo = useCallback(async () => {
    if (!bookingId || isJoining) return;
    setIsJoining(true);

    try {
      // 1. Get token from API
      const { data } = await generateToken({ variables: { bookingId } });
      const tokenResponse = data?.generateVideoToken;
      if (!tokenResponse?.token) {
        toast({
          kind: "error",
          title: "Failed to get video token",
          description: tokenResponse?.message ?? "Please try again.",
        });
        setIsJoining(false);
        return;
      }

      // 2. Show recording notice
      setPageState("notice");

      // 3. After notice, connect to room
      const token = tokenResponse.token;
      const roomName = tokenResponse.roomName;

      // Wait for the notice to complete (handled by the notice overlay)
      // The notice overlay will call connectToRoom when done
      // Store token/room in refs for the callback
      pendingConnectionRef.current = { token, roomName };
    } catch (err) {
      console.error("Failed to join video:", err);
      toast({
        kind: "error",
        title: "Failed to join session",
        description: "Check your connection and try again.",
      });
      setIsJoining(false);
    }
  }, [bookingId, isJoining, generateToken, toast]);

  const pendingConnectionRef = useRef<{ token: string; roomName: string } | null>(null);
  const isConnectingRef = useRef(false);

  const connectToRoom = useCallback(async () => {
    const pending = pendingConnectionRef.current;
    if (!pending) return;
    // Re-entry guard: notice 'ended' can fire more than once on some
    // browsers, and the no-notice + skip paths can race. Two TwilioVideo
    // .connect() calls with the same identity make Twilio drop one with
    // close-code 1001, leaving the room in a broken state. Clear the
    // pending ref so a duplicate call short-circuits.
    if (isConnectingRef.current || roomRef.current) return;
    isConnectingRef.current = true;
    pendingConnectionRef.current = null;

    try {
      // Dynamic import of twilio-video (avoids SSR issues, though portal is SPA)
      const TwilioVideo = await import("twilio-video");

      // Request media
      let localTracks: TwilioTrack[];
      try {
        const tracks = await TwilioVideo.createLocalTracks({
          audio: true,
          video: !isCameraOff,
        });
        localTracks = tracks as unknown as TwilioTrack[];
      } catch {
        // Camera denied, try audio-only
        try {
          const tracks = await TwilioVideo.createLocalTracks({
            audio: true,
            video: false,
          });
          localTracks = tracks as unknown as TwilioTrack[];
          setIsCameraOff(true);
          toast({
            kind: "info",
            title: "Camera unavailable",
            description: "Joining audio-only. Other participants can hear you but not see you.",
          });
        } catch {
          // Both denied -- fall back to phone
          toast({
            kind: "error",
            title: "Microphone access required",
            description: "Enable microphone access in your browser settings, or join by phone instead.",
          });
          setPageState("lobby");
          setIsJoining(false);
          isConnectingRef.current = false;
          return;
        }
      }

      localTracksRef.current = localTracks;

      // Connect to the room
      const room = await TwilioVideo.connect(pending.token, {
        name: pending.roomName,
        tracks: localTracks as unknown as TwilioTrack[],
        dominantSpeaker: true,
      });

      const typedRoom = room as unknown as TwilioRoom;
      roomRef.current = typedRoom;
      isConnectingRef.current = false;
      setPageState("in-session");
      setIsJoining(false);

      // Hand the local video track to React; VideoTile will mount and
      // attach() it once the in-session DOM commits.
      const lvt = localTracks.find((t) => t.kind === "video") ?? null;
      setLocalVideoTrack(lvt);
      if (!lvt) {
        setIsCameraOff(true);
      }

      // Twilio's docs explicitly recommend a beforeunload disconnect so
      // a tab close or reload doesn't leave a stale participant slot
      // holding the identity. Without this, a same-identity rejoin gets
      // booted by the server with WebSocket close-code 1001 once the
      // ghost session is reconciled.
      const beforeUnload = () => {
        typedRoom.disconnect();
      };
      window.addEventListener("beforeunload", beforeUnload);
      beforeUnloadHandlerRef.current = beforeUnload;

      // Handle existing participants
      typedRoom.participants.forEach((participant) => {
        handleParticipantConnected(participant);
      });

      // Handle new participants
      typedRoom.on("participantConnected", handleParticipantConnected);
      typedRoom.on("participantDisconnected", handleParticipantDisconnected);
      // Handle dominant speaker changes
      typedRoom.on("dominantSpeakerChanged", (participant?: TwilioParticipant) => {
        setDominantSpeaker(participant?.identity);
      });


      // Handle room disconnection
      typedRoom.on("disconnected", () => {
        localTracksRef.current.forEach((track) => {
          track.stop();
          // detach() only — the <video> elements belong to React
          // (VideoTile); el.remove() here rips them out of the DOM
          // mid-commit and React's own unmount then crashes with
          // removeChild NotFoundError (seen when End-meeting completed
          // the room server-side while still in-session).
          track.detach();
        });
        localTracksRef.current = [];
        roomRef.current = null;
        isConnectingRef.current = false;
        setLocalVideoTrack(null);
        setRemoteVideoTracks(new Map());
        setRemoteIdentities(new Set());
        setDominantSpeaker(undefined);
        if (beforeUnloadHandlerRef.current) {
          window.removeEventListener("beforeunload", beforeUnloadHandlerRef.current);
          beforeUnloadHandlerRef.current = null;
        }
      });

      // Subscribe to transcription events (Twilio SDK real-time transcription)
      (room as unknown as TwilioRoom).on("transcriptionReceived", (event: unknown) => {
        const transcriptionEvent = event as {
          track: { participant?: { identity: string } };
          transcriptionData: { transcript: string; final: boolean };
        };
        if (transcriptionEvent?.transcriptionData?.final) {
          const seq = ++transcriptSeqRef.current;
          const utterance: TranscriptUtterance = {
            eventId: crypto.randomUUID(),
            participantIdentity:
              transcriptionEvent.track?.participant?.identity ?? "Unknown",
            text: transcriptionEvent.transcriptionData.transcript,
            timestamp: Date.now(),
            sequence: seq,
          };
          setTranscriptEntries((prev) => [...prev, utterance]);
          transcriptBufferRef.current.push(utterance);
        }
      });
    } catch (err) {
      console.error("Failed to connect to room:", err);
      toast({
        kind: "error",
        title: "Connection failed",
        description: "Could not connect to the session. Try joining by phone instead.",
      });
      setPageState("lobby");
      setIsJoining(false);
      isConnectingRef.current = false;
    }
  }, [isCameraOff, toast]);

  // Remote video flows through React state and is rendered by VideoTile.
  // Audio stays imperative (appended to <body>) because it has no visual
  // surface and detaching individual <audio> elements doesn't require
  // React ownership.
  const attachTrack = useCallback((track: TwilioTrack, participantIdentity?: string) => {
    if (track.kind === "video") {
      // If participantIdentity is not provided, use a fallback
      const key = participantIdentity || `track-${track.kind}-${Date.now()}`;
      setRemoteVideoTracks((prev) => {
        const next = new Map(prev);
        next.set(key, track);
        return next;
      });
    } else if (track.kind === "audio") {
      const el = track.attach() as HTMLAudioElement;
      el.autoplay = true;
      document.body.appendChild(el);
      el.play().catch(() => {});
    }
  }, []);

  const detachRemoteTrack = useCallback((track: TwilioTrack, participantIdentity?: string) => {
    if (track.kind === "video") {
      // If participantIdentity is not provided, find the track by value
      setRemoteVideoTracks((prev) => {
        const next = new Map(prev);
        // If identity is provided, use it. Otherwise, find and remove by value.
        if (participantIdentity) {
          next.delete(participantIdentity);
        } else {
          for (const [key, value] of next) {
            if (value === track) {
              next.delete(key);
              break;
            }
          }
        }
        return next;
      });
    } else if (track.kind === "audio") {
      track.detach().forEach((el: HTMLMediaElement) => el.remove());
    }
  }, []);

  const handleParticipantConnected = useCallback(
    (participant: TwilioParticipant) => {
      setRemoteIdentities((prev) => {
        const next = new Set(prev);
        next.add(participant.identity);
        return next;
      });
      // Attach existing tracks with participant identity as the key
      participant.tracks.forEach((publication) => {
        if (publication.track) {
          attachTrack(publication.track, participant.identity);
        }
      });
      participant.on("trackSubscribed", (track: TwilioTrack) => {
        attachTrack(track, participant.identity);
      });
      participant.on("trackUnsubscribed", (track: TwilioTrack) => {
        detachRemoteTrack(track, participant.identity);
      });
      // Force a re-render to update participant list
      refetchSession();
    },
    [attachTrack, detachRemoteTrack, refetchSession],
  );

  const handleParticipantDisconnected = useCallback(
    (participant: TwilioParticipant) => {
      setRemoteIdentities((prev) => {
        if (!prev.has(participant.identity)) return prev;
        const next = new Set(prev);
        next.delete(participant.identity);
        return next;
      });
      // Detach all tracks for this participant when they disconnect
      participant.tracks.forEach((publication) => {
        if (publication.track) {
          detachRemoteTrack(publication.track, participant.identity);
        }
      });
      refetchSession();
    },
    [detachRemoteTrack, refetchSession],
  );

  // ── Mic/Camera toggle ─────────────────────────────────────────────
  const toggleMic = useCallback(() => {
    const audioTrack = localTracksRef.current.find((t) => t.kind === "audio");
    if (audioTrack) {
      if (isMuted) {
        audioTrack.enable();
      } else {
        audioTrack.disable();
      }
      setIsMuted(!isMuted);
    }
  }, [isMuted]);

  const toggleCamera = useCallback(() => {
    const videoTrack = localTracksRef.current.find((t) => t.kind === "video");
    if (videoTrack) {
      if (isCameraOff) {
        videoTrack.enable();
      } else {
        videoTrack.disable();
      }
      setIsCameraOff(!isCameraOff);
    }
  }, [isCameraOff]);

  // ── Recording toggle ──────────────────────────────────────────────
  const handleToggleRecording = useCallback(async () => {
    if (!bookingId || isTogglingRecording) return;
    const next = !recordingOn;
    setIsTogglingRecording(true);
    try {
      const { data } = await setSessionRecording({
        variables: { bookingId, enabled: next },
      });
      if (!data?.setSessionRecording?.success) {
        toast({
          kind: "error",
          title: data?.setSessionRecording?.error ?? "Could not update recording",
        });
      }
      await refetchSession();
    } catch (err) {
      console.error("Failed to toggle recording:", err);
      toast({ kind: "error", title: "Could not update recording" });
    } finally {
      setIsTogglingRecording(false);
    }
  }, [bookingId, isTogglingRecording, recordingOn, setSessionRecording, refetchSession, toast]);

  // ── Flush transcript buffer ────────────────────────────────────────
  const flushTranscriptBuffer = useCallback(async () => {
    if (!bookingId || transcriptBufferRef.current.length === 0) return;

    const batch = transcriptBufferRef.current.splice(0);
    const events = batch.map((u) => ({
      eventId: u.eventId,
      participantIdentity: u.participantIdentity,
      utteranceText: u.text,
      eventTimestamp: new Date(u.timestamp).toISOString(),
      confidence: null,
      isFinal: true,
      sequence: u.sequence,
    }));

    try {
      await saveTranscriptEvents({ variables: { bookingId, events } });
    } catch (err) {
      // Put events back so they are retried on the next flush
      transcriptBufferRef.current.unshift(...batch);
      console.error("Failed to flush transcript buffer:", err);
    }
  }, [bookingId, saveTranscriptEvents]);

  // Incremental flush: every 5 s OR when buffer hits 20 events
  useEffect(() => {
    if (pageState !== "in-session") return;

    // Interval-based: every 5 seconds
    transcriptFlushTimerRef.current = setInterval(() => {
      flushTranscriptBuffer();
    }, 5000);

    return () => {
      if (transcriptFlushTimerRef.current) {
        clearInterval(transcriptFlushTimerRef.current);
        transcriptFlushTimerRef.current = null;
      }
    };
  }, [pageState, flushTranscriptBuffer]);

  // Threshold-based: flush when buffer reaches 20 entries
  useEffect(() => {
    if (transcriptBufferRef.current.length >= 20) {
      flushTranscriptBuffer();
    }
  }, [transcriptEntries, flushTranscriptBuffer]);

  // ── End call ──────────────────────────────────────────────────────
  const handleEndCall = useCallback(async () => {
    // Flush remaining transcript buffer
    await flushTranscriptBuffer().catch((err) =>
      console.error("End-call transcript flush failed:", err),
    );

    // Save notes one final time
    if (bookingId && notesContent) {
      saveNotesMutation({ variables: { bookingId, content: notesContent } }).catch(() => {});
    }

    // Save action items one final time
    if (bookingId && actionItems.length > 0) {
      saveActionItems(actionItems);
    }

    // Disconnect from room
    if (roomRef.current) {
      roomRef.current.disconnect();
      roomRef.current = null;
    }

    // Stop local tracks. detach() only — React owns the <video> elements
    // (see the disconnected handler comment).
    localTracksRef.current.forEach((track) => {
      track.stop();
      track.detach();
    });
    localTracksRef.current = [];

    // Clear timers (and drop unsynced smoothing so a later rejoin doesn't
    // start the display inflated by this stint's accumulator)
    setLocalAccumulator(0);
    if (timerRef.current) {
      clearInterval(timerRef.current);
      timerRef.current = null;
    }
    if (transcriptFlushTimerRef.current) {
      clearInterval(transcriptFlushTimerRef.current);
      transcriptFlushTimerRef.current = null;
    }

    // Reset the rejoin guard so a future Rejoin → End cycle behaves the
    // same way (and so an externally-triggered end while we're still
    // somehow in-session can transition us out).
    rejoinedRef.current = false;
    setPageState("ended");
    refetchSession();
  }, [bookingId, notesContent, actionItems, saveNotesMutation, saveActionItems, refetchSession, flushTranscriptBuffer]);

  // ── End meeting for everyone ──────────────────────────────────────
  // Completes the Twilio room server-side: all customers are disconnected
  // (their clients route to the session-ended screen via TwilioError
  // 53118) and Twilio's room-ended webhook finalizes the session. On
  // mutation failure we stay in-session so the provider can retry or just
  // leave instead.
  const handleEndMeeting = useCallback(async () => {
    if (!bookingId || isEndingMeeting) return;
    setIsEndingMeeting(true);
    try {
      const { data } = await endSessionMeeting({ variables: { bookingId } });
      if (!data?.endSessionMeeting?.success) {
        toast({
          kind: "error",
          title: "Could not end the meeting",
          description: data?.endSessionMeeting?.error ?? "Try again, or leave the call instead.",
        });
        return;
      }
      setEndedForEveryone(true);
      await handleEndCall();
    } catch {
      toast({
        kind: "error",
        title: "Could not end the meeting",
        description: "Network error. Try again, or leave the call instead.",
      });
    } finally {
      setIsEndingMeeting(false);
      setEndMenuOpen(false);
    }
  }, [bookingId, isEndingMeeting, endSessionMeeting, handleEndCall, toast]);

  // ── Phone: Call Me ────────────────────────────────────────────────
  const handleCallMe = useCallback(async () => {
    if (!bookingId || isCallingMe) return;
    setIsCallingMe(true);
    try {
      const { data } = await triggerCallMeProvider({ variables: { bookingId } });
      if (data?.triggerCallMeProvider?.success) {
        toast({ kind: "success", title: "Calling your phone now" });
        setIsPhoneConnected(true);
        setPageState("in-session");
      } else {
        toast({
          kind: "error",
          title: "Call failed",
          description: data?.triggerCallMeProvider?.error ?? "Try manual dial-in instead.",
        });
      }
    } catch {
      toast({
        kind: "error",
        title: "Call failed",
        description: "Could not reach your phone. Try manual dial-in.",
      });
    } finally {
      setIsCallingMe(false);
    }
  }, [bookingId, isCallingMe, triggerCallMeProvider, toast]);

  // ── Send join link to client ──────────────────────────────────────
  const handleSendLink = useCallback(async () => {
    if (!bookingId || sendingLink || linkJustSent) return;
    try {
      const { data } = await sendSessionLink({ variables: { bookingId } });
      if (data?.sendSessionLink?.success) {
        toast({ kind: "success", title: "Join link sent to client" });
        setLinkJustSent(true);
        setTimeout(() => setLinkJustSent(false), 5000);
      } else {
        toast({
          kind: "error",
          title: "Couldn't send join link",
          description: data?.sendSessionLink?.error ?? undefined,
        });
      }
    } catch {
      toast({ kind: "error", title: "Couldn't send join link" });
    }
  }, [bookingId, sendingLink, linkJustSent, sendSessionLink, toast]);

  // ── Notice audio completion ───────────────────────────────────────
  const handleNoticeComplete = useCallback(() => {
    // Fire-and-forget: log notice-played event via private API
    if (bookingId) {
      logNoticePlayed({ variables: { bookingId } }).catch((err) =>
        console.error("logNoticePlayed failed:", err),
      );
    }

    connectToRoom();
  }, [bookingId, logNoticePlayed, connectToRoom]);

  const handleNoticeSkip = useCallback(() => {
    // Called when notice audio fails to load
    setNoticeError(true);
    // Per edge case #7: log warning, don't block the session
    connectToRoom();
  }, [connectToRoom]);

  // ── Participant connection status from events ─────────────────────
  const participantStatuses = useMemo(() => {
    const statuses: Record<string, { connected: boolean; joinMethod: string }> = {};
    for (const event of events) {
      if (!event.participantId) continue;
      if (event.eventType === "participant-connected") {
        statuses[event.participantId] = {
          connected: true,
          joinMethod: event.joinMethod ?? "video",
        };
      } else if (event.eventType === "participant-disconnected") {
        statuses[event.participantId] = {
          connected: false,
          joinMethod: event.joinMethod ?? "video",
        };
      }
    }
    return statuses;
  }, [events]);

  // ── Cleanup on unmount ────────────────────────────────────────────
  useEffect(() => {
    return () => {
      // Fire-and-forget the latest pending notes/action-items saves so the
      // last keystrokes aren't lost when the provider navigates away mid-debounce.
      if (
        bookingId &&
        pendingNotesContentRef.current !== null &&
        notesSaveTimerRef.current
      ) {
        saveNotesMutation({
          variables: { bookingId, content: pendingNotesContentRef.current },
        }).catch(() => {});
      }
      if (
        bookingId &&
        pendingActionItemsRef.current !== null &&
        actionSaveTimerRef.current
      ) {
        const items = pendingActionItemsRef.current;
        saveActionItemsMutation({
          variables: {
            bookingId,
            items: items.map((item, idx) => ({
              id: item.id,
              text: item.text,
              isCompleted: item.isCompleted,
              sortOrder: idx,
            })),
          },
        }).catch(() => {});
      }

      if (roomRef.current) {
        roomRef.current.disconnect();
      }
      localTracksRef.current.forEach((track) => {
        track.stop();
      });
      if (timerRef.current) clearInterval(timerRef.current);
      if (notesSaveTimerRef.current) clearTimeout(notesSaveTimerRef.current);
      if (actionSaveTimerRef.current) clearTimeout(actionSaveTimerRef.current);
      if (transcriptFlushTimerRef.current) clearInterval(transcriptFlushTimerRef.current);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // ── Dynamic poll cadence ──────────────────────────────────────────
  // Poll fast (3s) while we're waiting for the calls row to be provisioned;
  // back off to 10s once we're in lobby/in-session/etc. Apollo's
  // startPolling replaces the active interval.
  useEffect(() => {
    if (pageState === "no-session") {
      startSessionPolling(3000);
    } else {
      startSessionPolling(10000);
    }
  }, [pageState, startSessionPolling]);

  // ── Render ────────────────────────────────────────────────────────

  // Loading state
  if (pageState === "loading") {
    return (
      <div className="px-7 pt-10 pb-20">
        <div className="flex items-center gap-1.5 text-[12.5px] text-ink-4 mb-6">
          <ChevronLeft size={12} strokeWidth={2} />
          Back to session
        </div>
        <div className="animate-pulse space-y-6">
          <div className="h-10 w-96 rounded bg-canvas" />
          <div className="h-5 w-64 rounded bg-canvas" />
          <div className="grid grid-cols-12 gap-6">
            <div className="col-span-8 space-y-3">
              <div className="h-48 rounded-lg bg-canvas" />
              <div className="h-32 rounded-lg bg-canvas" />
            </div>
            <div className="col-span-4 space-y-3">
              <div className="h-40 rounded-lg bg-canvas" />
              <div className="h-32 rounded-lg bg-canvas" />
            </div>
          </div>
        </div>
      </div>
    );
  }

  // No session yet -- the row is being provisioned in the background. The
  // sessionStatus query is already polling every 10s, so as soon as it appears
  // the page will transition to the lobby on its own. We just wait politely.
  if (pageState === "no-session") {
    return (
      <div className="px-7 pt-10 pb-20">
        <Link
          to={bookingId ? `/sessions/${bookingId}` : "/sessions"}
          className="inline-flex items-center gap-1.5 text-[12.5px] text-ink-4 hover:text-ink-2 transition-colors mb-6"
        >
          <ChevronLeft size={12} strokeWidth={2} />
          Back to session
        </Link>

        <div className="max-w-lg mx-auto mt-12">
          <div className="bg-paper-hi rounded-lg shadow-card p-8 text-center">
            <div className="w-16 h-16 rounded-full bg-canvas text-ink-3 flex items-center justify-center mx-auto mb-4">
              <Loader2 size={28} strokeWidth={2} className="animate-spin" />
            </div>
            <h1 className="font-display text-[24px] font-medium mb-2">
              Setting up your session
            </h1>
            <p className="text-[13.5px] text-ink-3 leading-relaxed">
              We're getting things ready. This usually takes a few seconds — the page will continue automatically once it's done.
            </p>
          </div>
        </div>
      </div>
    );
  }

  // Post-session state
  if (pageState === "ended") {
    return (
      <div className="px-7 pt-10 pb-20 max-w-2xl mx-auto">
        <div className="bg-paper-hi rounded-lg shadow-card p-8 text-center">
          <div className="w-16 h-16 rounded-full bg-sage-s text-sage flex items-center justify-center mx-auto mb-4">
            <Check size={28} strokeWidth={2} />
          </div>
          <h1 className="font-display text-[28px] font-medium mb-2">Session ended</h1>
          <p className="text-[14px] text-ink-3 mb-6">
            {booking?.bookingType?.name ?? "Session"} with{" "}
            {primaryCustomer ? clientDisplayName(primaryCustomer) : (booking?.primaryCustomerName ?? "Client")}
          </p>
          <div className="grid grid-cols-2 gap-4 max-w-xs mx-auto mb-8 text-[13px]">
            <div>
              <div className="text-[10.5px] uppercase tracking-wider text-ink-4 font-medium mb-1">
                Duration
              </div>
              <div className="font-display text-[20px] font-medium num">
                {formatElapsed(elapsedSeconds)}
              </div>
            </div>
            <div>
              <div className="text-[10.5px] uppercase tracking-wider text-ink-4 font-medium mb-1">
                Participants
              </div>
              <div className="font-display text-[20px] font-medium num">
                {Object.values(participantStatuses).length || 2}
              </div>
            </div>
          </div>
          {notesContent && (
            <div className="text-left bg-paper rounded-lg p-4 mb-6 text-[13px]">
              <div className="text-[10.5px] uppercase tracking-wider text-ink-4 font-medium mb-2">
                Session notes saved
              </div>
              <p className="text-ink-2 line-clamp-3">{notesContent}</p>
            </div>
          )}
          {actionItems.length > 0 && (
            <div className="text-left bg-paper rounded-lg p-4 mb-6">
              <div className="text-[10.5px] uppercase tracking-wider text-ink-4 font-medium mb-2">
                Action items ({actionItems.length})
              </div>
              <div className="space-y-1.5">
                {actionItems.map((item) => (
                  <div key={item.id} className="flex items-start gap-2 text-[12.5px]">
                    <div
                      className={`w-3.5 h-3.5 mt-0.5 rounded border flex-shrink-0 flex items-center justify-center ${
                        item.isCompleted
                          ? "bg-sage border-sage text-paper-hi"
                          : "border-ink-5"
                      }`}
                    >
                      {item.isCompleted && <Check size={8} strokeWidth={3} />}
                    </div>
                    <span className={item.isCompleted ? "text-ink-4 line-through" : "text-ink-2"}>
                      {item.text}
                    </span>
                  </div>
                ))}
              </div>
            </div>
          )}
          {!endedForEveryone && (
            <p className="text-[12px] text-ink-4 mb-4">
              Left by accident, or pausing a multi-part consultation? You can
              rejoin — the session resumes where it left off.
            </p>
          )}
          <div className="flex items-center gap-3 justify-center flex-wrap">
            <Link
              to={`/sessions/${bookingId}/close`}
              className="px-5 py-2.5 rounded-md bg-tide text-paper-hi text-[13px] font-medium hover:bg-tide-d"
            >
              Close session
            </Link>
            {/* Rejoin stays available after leaves and room timeouts —
                sessions are resumable (multi-part consultations) until the
                provider explicitly ends the meeting or the booking is
                closed; on stale state the server refuses with a friendly
                "already ended" message. */}
            {!endedForEveryone && (
              <button
                onClick={() => {
                  // Resume from where we left off — keep notes, action items,
                  // transcript, and the elapsed timer; the join flow handles
                  // the rest. If the Twilio Room has actually finalized, the
                  // generateVideoToken call from the lobby will surface an
                  // error toast.
                  rejoinedRef.current = true;
                  refetchSession();
                  setPageState("lobby");
                }}
                className="px-5 py-2.5 rounded-md hair border bg-paper-hi text-[13px] text-ink-2 font-medium hover:bg-paper-2"
              >
                Rejoin session
              </button>
            )}
            <Link
              to={`/sessions/${bookingId}`}
              className="px-5 py-2.5 rounded-md hair border bg-paper-hi text-[13px] text-ink-2 font-medium hover:bg-paper-2"
            >
              Back to session
            </Link>
            <Link
              to="/sessions"
              className="px-5 py-2.5 rounded-md hair border bg-paper-hi text-[13px] text-ink-2 font-medium hover:bg-paper-2"
            >
              All sessions
            </Link>
          </div>
        </div>
      </div>
    );
  }

  // ── Recording notice overlay ──────────────────────────────────────
  if (pageState === "notice") {
    return (
      <div className="fixed inset-0 z-50 bg-ink/90 flex items-center justify-center p-6">
        <div className="bg-paper-hi rounded-lg shadow-card p-8 max-w-md w-full text-center">
          <div className="w-14 h-14 rounded-full bg-rust-s text-rust flex items-center justify-center mx-auto mb-4">
            <AlertCircle size={24} strokeWidth={2} />
          </div>
          <h2 className="font-display text-[22px] font-medium mb-2">Recording notice</h2>
          <p className="text-[14px] text-ink-3 mb-6">
            Please listen to the following notice before joining the session.
          </p>
          {!noticeError ? (
            <RecordingNoticeAudio
              noticeUrl={recordingNoticeUrl}
              onComplete={handleNoticeComplete}
              onError={handleNoticeSkip}
            />
          ) : (
            <div className="bg-spark-s rounded-md p-4 mb-4 text-[13px] text-spark">
              <p className="font-medium mb-1">Audio notice unavailable</p>
              <p className="text-[12px]">
                This session may be recorded. By joining, you acknowledge the recording policy.
              </p>
            </div>
          )}
          {noticeError && (
            <button
              type="button"
              onClick={handleNoticeComplete}
              className="px-5 py-2.5 rounded-md bg-tide text-paper-hi text-[13px] font-medium hover:bg-tide-d"
            >
              I understand, join session
            </button>
          )}
        </div>
      </div>
    );
  }

  // ── Main layout (lobby + in-session share the same chrome) ────────
  const isInSession = pageState === "in-session";
  const isLobby = pageState === "lobby";

  return (
    <div className="flex flex-col min-h-screen">
      {/* Top bar */}
      <header className="hair-b h-[72px] px-6 flex items-center justify-between bg-paper-hi/95 backdrop-blur sticky top-0 z-20 flex-shrink-0">
        <div className="flex items-center gap-4 min-w-0">
          <Link
            to={`/sessions/${bookingId}`}
            className="w-9 h-9 rounded-md hair border bg-paper-hi hover:bg-paper-2 flex items-center justify-center text-ink-3 flex-shrink-0"
            aria-label="Back to session"
            onClick={(e) => {
              if (isInSession) {
                if (!window.confirm("You are in an active session. Leaving this page will disconnect you. Continue?")) {
                  e.preventDefault();
                }
              }
            }}
          >
            <ChevronLeft size={14} strokeWidth={2} />
          </Link>
          <div className="min-w-0">
            <div className="flex items-center gap-2 mb-0.5">
              {isInSession && (
                <span className="flex items-center gap-1.5 text-[10.5px] uppercase tracking-wider font-semibold text-sage">
                  <span className="w-2 h-2 rounded-full bg-sage pulse-soft" />
                  {isPhoneConnected ? "Connected by phone" : "In session"}
                </span>
              )}
              {isInSession && (
                recordingOn ? (
                  <span
                    className="flex items-center gap-1.5 text-[10.5px] uppercase tracking-wider font-bold text-rust"
                    title="This session is being recorded"
                  >
                    <span className="w-2 h-2 rounded-full bg-rust pulse-soft" />
                    Rec
                  </span>
                ) : (
                  <span
                    className="flex items-center gap-1.5 text-[10.5px] uppercase tracking-wider font-semibold text-ink-3"
                    title="This session is not being recorded"
                  >
                    <span className="w-2 h-2 rounded-full bg-ink-3" />
                    Not recording
                  </span>
                )
              )}
              {isLobby && (
                <span className="flex items-center gap-1.5 text-[10.5px] uppercase tracking-wider font-semibold text-tide">
                  <span className="w-2 h-2 rounded-full bg-tide" />
                  Lobby
                </span>
              )}
            </div>
            <div className="flex items-baseline gap-3 flex-wrap min-w-0">
              <h1 className="font-display text-[20px] font-medium leading-none truncate">
                {booking?.bookingType?.name ?? "Session"}
              </h1>
              <span className="text-[13px] text-ink-3 truncate">
                with <span className="font-medium">{primaryCustomer ? clientDisplayName(primaryCustomer) : (booking?.primaryCustomerName ?? "Client")}</span>
              </span>
            </div>
          </div>
        </div>

        <div className="flex items-center gap-4 flex-shrink-0">
          {/* Timer */}
          {isInSession && (
            <>
              <div className="text-right">
                <div className="text-[10.5px] uppercase tracking-wider font-medium mb-0.5">
                  {timerPaused ? (
                    <span className="text-ink-4">Waiting for customer</span>
                  ) : (
                    <span className="text-ink-4">Elapsed</span>
                  )}
                </div>
                <div className="flex items-baseline gap-1.5 mt-0.5">
                  <span className={`font-display text-[22px] font-medium num ${timerPaused ? 'text-ink-4' : 'text-tide'}`}>
                    {formatElapsed(elapsedSeconds)}
                  </span>
                  {timerPaused && (
                    <span className="text-[11px] text-tide font-medium">paused</span>
                  )}
                </div>
              </div>
              <div className="w-px h-10 bg-ink/10" />
            </>
          )}

          {/* Controls */}
          {isInSession && !isPhoneConnected && (
            <div className="flex items-center gap-2">
              <button
                type="button"
                onClick={() => setIsMaximized((v) => !v)}
                className="w-9 h-9 rounded-md flex items-center justify-center hair border bg-paper-hi hover:bg-paper-2 text-ink-3"
                aria-label={isMaximized ? "Exit full view" : "Maximize video"}
                title={isMaximized ? "Exit full view" : "Maximize video"}
              >
                {isMaximized ? (
                  <Minimize2 size={14} strokeWidth={2} />
                ) : (
                  <Maximize2 size={14} strokeWidth={2} />
                )}
              </button>
              <button
                type="button"
                onClick={toggleMic}
                className={`w-9 h-9 rounded-md flex items-center justify-center ${
                  isMuted
                    ? "bg-rust text-paper-hi"
                    : "hair border bg-paper-hi hover:bg-paper-2 text-ink-3"
                }`}
                aria-label={isMuted ? "Unmute" : "Mute"}
              >
                {isMuted ? <MicOff size={14} strokeWidth={2} /> : <Mic size={14} strokeWidth={2} />}
              </button>
              <button
                type="button"
                onClick={toggleCamera}
                className={`w-9 h-9 rounded-md flex items-center justify-center ${
                  isCameraOff
                    ? "bg-ink-3 text-paper-hi"
                    : "hair border bg-paper-hi hover:bg-paper-2 text-ink-3"
                }`}
                aria-label={isCameraOff ? "Camera on" : "Camera off"}
              >
                {isCameraOff ? (
                  <VideoOff size={14} strokeWidth={2} />
                ) : (
                  <VideoIcon size={14} strokeWidth={2} />
                )}
              </button>
              {recordingAvailable && (
                <button
                  type="button"
                  onClick={handleToggleRecording}
                  disabled={isTogglingRecording}
                  className={`w-9 h-9 rounded-md flex items-center justify-center ${
                    recordingOn
                      ? "bg-rust text-paper-hi"
                      : "hair border bg-paper-hi hover:bg-paper-2 text-ink-3"
                  } ${isTogglingRecording ? "opacity-60" : ""}`}
                  aria-label={recordingOn ? "Pause recording" : "Resume recording"}
                  title={recordingOn ? "Pause recording" : "Resume recording"}
                >
                  <Disc size={14} strokeWidth={2} />
                </button>
              )}
              <EndCallMenu
                label="End call"
                open={endMenuOpen}
                setOpen={setEndMenuOpen}
                onLeave={handleEndCall}
                onEndMeeting={handleEndMeeting}
                isEndingMeeting={isEndingMeeting}
              />
            </div>
          )}

          {isInSession && isPhoneConnected && (
            <div className="flex items-center gap-2">
              {recordingAvailable && (
                <button
                  type="button"
                  onClick={handleToggleRecording}
                  disabled={isTogglingRecording}
                  className={`w-9 h-9 rounded-md flex items-center justify-center ${
                    recordingOn
                      ? "bg-rust text-paper-hi"
                      : "hair border bg-paper-hi hover:bg-paper-2 text-ink-3"
                  } ${isTogglingRecording ? "opacity-60" : ""}`}
                  aria-label={recordingOn ? "Pause recording" : "Resume recording"}
                  title={recordingOn ? "Pause recording" : "Resume recording"}
                >
                  <Disc size={14} strokeWidth={2} />
                </button>
              )}
              <EndCallMenu
                label="End session"
                open={endMenuOpen}
                setOpen={setEndMenuOpen}
                onLeave={handleEndCall}
                onEndMeeting={handleEndMeeting}
                isEndingMeeting={isEndingMeeting}
              />
            </div>
          )}
        </div>
      </header>

      {/* Workspace grid */}
      <div className="flex-1 grid grid-cols-1 lg:grid-cols-12 gap-0 min-h-0">
        {/* Left rail: client + docs + dial-in */}
        {!isMaximized && (
        <aside className="lg:col-span-3 hair-r p-5 space-y-4 bg-paper overflow-y-auto">
          {/* Client compact */}
          {primaryCustomer && (
            <div className="bg-paper-hi rounded-lg shadow-card p-4">
              <div className="flex items-center gap-3 mb-3">
                <Avatar initials={customerInitials} size="lg" className="bg-tide text-paper-hi" />
                <div className="min-w-0 flex-1">
                  <div className="flex items-center gap-2">
                    <span className="text-[13.5px] font-medium truncate">
                      {clientDisplayName(primaryCustomer)}
                    </span>
                  </div>
                  {primaryCustomer.email && (
                    <div className="text-[11px] text-ink-4 truncate">{primaryCustomer.email}</div>
                  )}
                </div>
              </div>
              <div className="hair-t pt-3 space-y-1.5 text-[11.5px]">
                <div className="flex items-center justify-between">
                  <span className="text-ink-4">Matter</span>
                  <span className="text-ink-2 font-medium">
                    {booking?.bookingType?.name ?? "N/A"}
                  </span>
                </div>
                <div className="flex items-center justify-between">
                  <span className="text-ink-4">Provider</span>
                  <span className="text-ink-2 font-medium">{providerName ?? "N/A"}</span>
                </div>
                {avail && (
                  <div className="flex items-center justify-between">
                    <span className="text-ink-4">Scheduled</span>
                    <span className="text-ink-2 font-medium num">
                      {formatTimeRange(avail.date, avail.startTime, avail.endTime)}
                    </span>
                  </div>
                )}
                <div className="flex items-center justify-between">
                  <span className="text-ink-4">Method</span>
                  <span className="text-ink-2 font-medium">
                    {methodToLocation(booking?.method ?? "video")}
                  </span>
                </div>
              </div>
              <button
                type="button"
                onClick={handleSendLink}
                disabled={sendingLink || linkJustSent}
                className="mt-3 w-full px-3 py-2 rounded-md hair border bg-paper text-[12px] text-ink-2 hover:bg-paper-2 flex items-center justify-center gap-1.5 font-medium disabled:opacity-50"
              >
                {linkJustSent ? (
                  <Check size={12} strokeWidth={2} />
                ) : (
                  <Send size={12} strokeWidth={2} />
                )}
                {sendingLink
                  ? "Sending…"
                  : linkJustSent
                    ? "They'll get it shortly"
                    : "Send join link to client"}
              </button>
            </div>
          )}

          {/* Participant list with connection status */}
          <div className="bg-paper-hi rounded-lg shadow-card overflow-hidden">
            <div className="px-4 py-3 hair-b flex items-center justify-between">
              <h3 className="text-[12.5px] font-semibold flex items-center gap-1.5">
                <Users size={12} strokeWidth={2} />
                Participants
              </h3>
              <span className="text-[10.5px] text-ink-4 num">
                {accessCodes.length}
              </span>
            </div>
            <div className="divide-hair">
              {/* Provider */}
              {providerCode && (
                <div className="px-4 py-3 flex items-center gap-3">
                  <Avatar initials={providerInitials} size="sm" />
                  <div className="flex-1 min-w-0">
                    <div className="text-[12.5px] font-medium truncate">
                      {providerName ?? "Provider"} (you)
                    </div>
                    <div className="flex items-center gap-2 mt-0.5">
                      <span className="text-[10.5px] font-mono text-ink-4 num">
                        {providerCode.code}
                      </span>
                      {participantStatuses[providerCode.participantId] && (
                        <span className="flex items-center gap-1 text-[10.5px]">
                          <span
                            className={`dot ${
                              participantStatuses[providerCode.participantId].connected
                                ? "bg-sage"
                                : "bg-ink-5"
                            }`}
                          />
                          <span className="text-ink-4">
                            {participantStatuses[providerCode.participantId].connected
                              ? "Connected"
                              : "Disconnected"}
                          </span>
                        </span>
                      )}
                    </div>
                  </div>
                  {participantStatuses[providerCode.participantId]?.joinMethod === "phone" ? (
                    <Phone size={12} className="text-ink-4" />
                  ) : (
                    <VideoIcon size={12} className="text-ink-4" />
                  )}
                </div>
              )}
              {/* Customers */}
              {customerCodes.map((code) => {
                const status = participantStatuses[code.participantId];
                const customer = customerByParticipantId.get(code.participantId);
                const rowName = customer
                  ? clientDisplayName(customer)
                  : primaryCustomer
                    ? clientDisplayName(primaryCustomer)
                    : "Client";
                const rowInitials = customer
                  ? clientInitials(customer)
                  : customerInitials;
                return (
                  <div key={code.id} className="px-4 py-3 flex items-center gap-3">
                    <Avatar
                      initials={rowInitials}
                      size="sm"
                      className="bg-tide text-paper-hi"
                    />
                    <div className="flex-1 min-w-0">
                      <div className="text-[12.5px] font-medium truncate">
                        {rowName}
                      </div>
                      <div className="flex items-center gap-2 mt-0.5">
                        <span className="text-[10.5px] font-mono text-ink-4 num">
                          {code.code}
                        </span>
                        {status && (
                          <span className="flex items-center gap-1 text-[10.5px]">
                            <span
                              className={`dot ${status.connected ? "bg-sage" : "bg-ink-5"}`}
                            />
                            <span className="text-ink-4">
                              {status.connected ? "Connected" : "Offline"}
                            </span>
                          </span>
                        )}
                        {!status && (
                          <span className="flex items-center gap-1 text-[10.5px]">
                            <span className="dot bg-ink-5" />
                            <span className="text-ink-4">Not joined</span>
                          </span>
                        )}
                      </div>
                    </div>
                    {status?.joinMethod === "phone" ? (
                      <Phone size={12} className="text-ink-4" />
                    ) : (
                      <VideoIcon size={12} className="text-ink-4" />
                    )}
                  </div>
                );
              })}
            </div>
          </div>

          {/* Dial-in info card (hidden when tenant has no phone number) */}
          {tenantPhoneNumber && (
            <div className="bg-paper-hi rounded-lg shadow-card overflow-hidden">
              <div className="px-4 py-3 hair-b flex items-center justify-between">
                <h3 className="text-[12.5px] font-semibold flex items-center gap-1.5">
                  <Phone size={12} strokeWidth={2} />
                  Phone dial-in
                </h3>
              </div>
              <div className="p-4 space-y-2.5 text-[12px]">
                <div>
                  <div className="text-[10.5px] uppercase tracking-wider text-ink-4 font-medium mb-0.5">
                    Number
                  </div>
                  <div className="font-mono text-ink-2 font-medium num">{tenantPhoneNumber}</div>
                </div>
                {providerCode && (
                  <div>
                    <div className="text-[10.5px] uppercase tracking-wider text-ink-4 font-medium mb-0.5">
                      Your code
                    </div>
                    <div className="font-mono text-ink-2 font-medium num">{providerCode.code}</div>
                  </div>
                )}
                <div className="text-[10.5px] text-ink-4">
                  Share with clients who need to dial in.
                </div>
              </div>
            </div>
          )}

          {/* Documents */}
          {files.length > 0 && (
            <div className="bg-paper-hi rounded-lg shadow-card overflow-hidden">
              <div className="px-4 py-3 hair-b flex items-center justify-between">
                <h3 className="text-[12.5px] font-semibold">Documents</h3>
                <span className="text-[10.5px] text-ink-4 num">{files.length}</span>
              </div>
              <div className="divide-hair">
                {files.map(
                  (file: {
                    id: string;
                    originalFilename: string;
                    fileSize: number;
                    mimeType: string;
                    createdAt: string;
                  }) => (
                    <div
                      key={file.id}
                      className="px-3 py-2.5 flex items-center gap-2.5 hover:bg-paper-2 transition"
                    >
                      <div
                        className={`w-7 h-8 rounded flex items-center justify-center flex-shrink-0 ${
                          file.mimeType.includes("pdf")
                            ? "bg-rust-s text-rust"
                            : "bg-sage-s text-sage"
                        }`}
                      >
                        <FileText size={12} strokeWidth={2} />
                      </div>
                      <div className="min-w-0 flex-1">
                        <div className="text-[12px] font-medium truncate">
                          {file.originalFilename}
                        </div>
                        <div className="text-[10.5px] text-ink-4">
                          {formatFileSize(file.fileSize)}
                        </div>
                      </div>
                    </div>
                  ),
                )}
              </div>
            </div>
          )}
        </aside>
        )}

        {/* Center: video/lobby + notes */}
        <section
          className={`${
            isMaximized ? "lg:col-span-12" : "lg:col-span-6"
          } flex flex-col bg-paper-hi min-w-0`}
        >
          {/* Video / lobby area */}
          {isLobby && (
            <LobbyView
              modality={modality}
              setModality={setModality}
              booking={booking}
              customerName={primaryCustomer ? clientDisplayName(primaryCustomer) : "Client"}
              customerInitials={customerInitials}
              providerInitials={providerInitials}
              isJoining={isJoining}
              isCallingMe={isCallingMe}
              customerHasJoined={customerCodes.some(
                (c) => participantStatuses[c.participantId]?.connected,
              )}
              onJoinVideo={handleJoinVideo}
              onCallMe={handleCallMe}
              tenantPhoneNumber={tenantPhoneNumber}
              providerCode={providerCode}
            />
          )}

          {isInSession && !isPhoneConnected && (
            <VideoView
              remoteIdentities={remoteIdentities}
              remoteVideoTracks={remoteVideoTracks}
              customerByIdentity={customerByIdentity}
              dominantSpeaker={dominantSpeaker}
              localVideoTrack={localVideoTrack}
              customerName={primaryCustomer ? clientDisplayName(primaryCustomer) : "Client"}
              customerInitials={customerInitials}
              providerInitials={providerInitials}
              isCameraOff={isCameraOff}
              isMuted={isMuted}
              remoteJoinMethod={
                customerCodes
                  .map((c) => participantStatuses[c.participantId])
                  .find((s) => s?.connected)?.joinMethod as "phone" | "video" | undefined
                ?? null
              }
            />
          )}

          {isInSession && isPhoneConnected && (
            <PhoneConnectedView
              customerName={primaryCustomer ? clientDisplayName(primaryCustomer) : "Client"}
              customerInitials={customerInitials}
              providerInitials={providerInitials}
              onSwitchToVideo={handleJoinVideo}
              timerPaused={timerPaused}
            />
          )}

          {/* Notes / Transcript tabs */}
          {(isInSession || isLobby) && !isMaximized && (
            <div className="flex-1 flex flex-col min-h-0">
              <div className="px-6 pt-4 hair-b">
                <div className="flex items-center gap-6">
                  <SegmentedControl
                    options={["Notes", "Transcript"]}
                    value={notesTab === "notes" ? "Notes" : "Transcript"}
                    onChange={(v) =>
                      setNotesTab(v === "Notes" ? "notes" : "transcript")
                    }
                  />
                  <span className="ml-auto pb-0 flex items-center gap-2 text-[11.5px] text-ink-4">
                    {notesSaveStatus === "saving" && (
                      <span className="flex items-center gap-1">
                        <Loader2 size={10} className="animate-spin" />
                        Saving...
                      </span>
                    )}
                    {notesSaveStatus === "saved" && (
                      <span className="flex items-center gap-1">
                        <span className="dot bg-sage" />
                        Saved
                      </span>
                    )}
                    {notesSaveStatus === "idle" && isInSession && (
                      <span className="flex items-center gap-1">
                        <span className="dot bg-ink-5" />
                        Auto-save enabled
                      </span>
                    )}
                  </span>
                </div>
              </div>

              <div className="flex-1 overflow-y-auto p-6">
                {notesTab === "notes" && (
                  <textarea
                    value={notesContent}
                    onChange={(e) => handleNotesChange(e.target.value)}
                    placeholder={
                      isLobby
                        ? "Session notes will be available when you join..."
                        : "Type your session notes here. Auto-saved every 2 seconds."
                    }
                    className="w-full min-h-[200px] p-4 rounded-md bg-paper hair border text-[14px] text-ink-2 placeholder:text-ink-5 focus:outline-none focus:border-tide resize-none leading-relaxed"
                    readOnly={isLobby}
                  />
                )}

                {notesTab === "transcript" && (
                  <div className="space-y-3">
                    {transcriptEntries.length === 0 && (
                      <div className="py-8 text-center text-[13px] text-ink-4">
                        {isInSession
                          ? "Live transcript will appear here as the conversation progresses."
                          : "Transcript will be available during the session."}
                      </div>
                    )}
                    {transcriptEntries.map((entry, idx) => (
                      <div key={idx} className="flex gap-3">
                        <div className="flex-shrink-0 w-20 text-right">
                          <span className="text-[10.5px] font-mono text-ink-4 num">
                            {new Date(entry.timestamp).toLocaleTimeString([], {
                              hour: "2-digit",
                              minute: "2-digit",
                            })}
                          </span>
                          <div className="text-[10px] text-ink-5 truncate">
                            {identityToLabel(entry.participantIdentity, customerByIdentity)}
                          </div>
                        </div>
                        <p className="flex-1 text-[13px] text-ink-2 leading-relaxed">
                          {entry.text}
                        </p>
                      </div>
                    ))}
                  </div>
                )}
              </div>
            </div>
          )}
        </section>

        {/* Right rail: action items (visible in-session) */}
        {!isMaximized && (
        <aside className="lg:col-span-3 hair-l p-5 space-y-4 bg-paper overflow-y-auto">
          {/* Session info card (in-session) */}
          {/* Lobby: scheduling info */}
          {isLobby && avail && (
            <div className="bg-paper-hi rounded-lg shadow-card p-4">
              <div className="text-[10.5px] uppercase tracking-wider font-semibold text-ink-4 mb-2">
                Scheduled
              </div>
              <div className="font-display text-[18px] font-medium num leading-tight">
                {formatTimeRange(avail.date, avail.startTime, avail.endTime)}
              </div>
              <div className="text-[12px] text-ink-3 mt-1">
                {formatDateLabel(avail.date)} ·{" "}
                {formatDuration(avail.startTime, avail.endTime, avail.date)}
              </div>
            </div>
          )}

          {/* Action items — single consolidated editor (capture + tick off
              + remove). */}
          <div className="bg-paper-hi rounded-lg shadow-card overflow-hidden">
            <div className="px-4 py-3 hair-b flex items-center justify-between">
              <h3 className="text-[13px] font-semibold flex items-center gap-1.5">
                <Check size={12} strokeWidth={2} />
                Action items
              </h3>
              <span className="text-[10.5px] text-ink-4 num">{actionItems.length}</span>
            </div>
            <div className="p-3 hair-b flex items-center gap-2">
              <input
                type="text"
                value={newItemText}
                onChange={(e) => setNewItemText(e.target.value)}
                onKeyDown={(e) => {
                  if (e.key === "Enter") addActionItem();
                }}
                placeholder="Add an action item..."
                className="flex-1 min-w-0 px-2.5 py-1.5 rounded-md bg-paper hair border text-[12.5px] text-ink placeholder:text-ink-5 focus:outline-none focus:border-tide"
              />
              <button
                type="button"
                onClick={addActionItem}
                disabled={!newItemText.trim()}
                className="p-1.5 rounded-md bg-tide text-paper-hi hover:bg-tide-d disabled:opacity-50 disabled:cursor-not-allowed flex-shrink-0"
                aria-label="Add action item"
              >
                <Plus size={13} strokeWidth={2} />
              </button>
            </div>
            {actionItems.length === 0 ? (
              <div className="p-4 text-center text-[12px] text-ink-4">
                No action items yet
              </div>
            ) : (
              <div className="divide-hair max-h-[340px] overflow-y-auto">
                {actionItems.map((item) => (
                  <div key={item.id} className="px-4 py-2.5 group">
                    <div className="flex items-start gap-2.5">
                      <button
                        type="button"
                        onClick={() => toggleActionItem(item.id)}
                        className={`w-4 h-4 mt-0.5 rounded border-2 flex-shrink-0 flex items-center justify-center transition ${
                          item.isCompleted
                            ? "bg-sage border-sage text-paper-hi"
                            : "border-ink-5 hover:border-tide"
                        }`}
                        aria-label={item.isCompleted ? "Mark incomplete" : "Mark complete"}
                      >
                        {item.isCompleted && <Check size={8} strokeWidth={3} />}
                      </button>
                      <div className="flex-1 min-w-0">
                        <div
                          className={`text-[12.5px] leading-snug ${
                            item.isCompleted ? "text-ink-4 line-through" : "text-ink-2"
                          }`}
                        >
                          {item.text}
                        </div>
                      </div>
                      <button
                        type="button"
                        onClick={() => removeActionItem(item.id)}
                        className="text-ink-5 hover:text-rust opacity-0 group-hover:opacity-100 transition-opacity flex-shrink-0 mt-0.5"
                        aria-label="Remove"
                      >
                        <X size={12} strokeWidth={2} />
                      </button>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>

          {/* Session events log */}
          {events.length > 0 && (
            <div className="bg-paper-hi rounded-lg shadow-card overflow-hidden">
              <div className="px-4 py-3 hair-b">
                <h3 className="text-[12.5px] font-semibold">Session events</h3>
              </div>
              <div className="divide-hair max-h-[200px] overflow-y-auto">
                {events
                  .slice()
                  .reverse()
                  .slice(0, 10)
                  .map((event) => (
                    <div key={event.id} className="px-4 py-2 flex items-center gap-2 text-[11.5px]">
                      <span
                        className={`dot ${
                          event.eventType.includes("connected")
                            ? "bg-sage"
                            : event.eventType.includes("disconnected")
                              ? "bg-rust"
                              : "bg-tide"
                        }`}
                      />
                      <span className="text-ink-3 truncate flex-1">
                        {event.eventType.replace(/-/g, " ")}
                        {event.participantRole && ` (${event.participantRole})`}
                      </span>
                      <span className="text-ink-5 font-mono num text-[10px] flex-shrink-0">
                        {event.occurredAt
                          ? new Date(event.occurredAt).toLocaleTimeString([], {
                              hour: "2-digit",
                              minute: "2-digit",
                            })
                          : ""}
                      </span>
                    </div>
                  ))}
              </div>
            </div>
          )}
        </aside>
        )}
      </div>

      {/* Footer */}
      {!isMaximized && (
        <footer className="hair-t h-10 px-6 flex items-center justify-end bg-paper text-[11.5px] text-ink-4 flex-shrink-0">
          {isInSession && (
            <span className="flex items-center gap-1 text-sage font-medium">
              <span className="dot bg-sage" />
              Connected
            </span>
          )}
          {isLobby && <span className="text-ink-4">Ready to join</span>}
        </footer>
      )}
    </div>
  );
}

// ── Sub-components ──────────────────────────────────────────────────

// End-call button with a two-option menu: leaving keeps the room live for
// customers (provider can rejoin); ending the meeting completes the Twilio
// room server-side and sends every customer to their session-ended screen.
function EndCallMenu({
  label,
  open,
  setOpen,
  onLeave,
  onEndMeeting,
  isEndingMeeting,
}: {
  label: string;
  open: boolean;
  setOpen: (v: boolean) => void;
  onLeave: () => void;
  onEndMeeting: () => void;
  isEndingMeeting: boolean;
}) {
  return (
    <div className="relative">
      <button
        type="button"
        onClick={() => setOpen(!open)}
        className="px-3.5 py-2 rounded-md bg-rust text-paper-hi text-[12.5px] hover:bg-[#8F3B22] font-medium flex items-center gap-1.5"
      >
        <PhoneOff size={12} strokeWidth={2.2} />
        {label}
      </button>
      {open && (
        <>
          <div
            className="fixed inset-0 z-40"
            onClick={() => setOpen(false)}
            aria-hidden="true"
          />
          <div className="absolute right-0 top-full mt-1.5 z-50 w-72 rounded-lg bg-paper-hi shadow-card hair border p-1.5">
            <button
              type="button"
              onClick={() => {
                setOpen(false);
                onLeave();
              }}
              className="w-full text-left px-3 py-2.5 rounded-md hover:bg-paper-2 flex items-start gap-2.5"
            >
              <PhoneOff size={14} strokeWidth={2} className="mt-0.5 shrink-0 text-ink-3" />
              <span>
                <span className="block text-[12.5px] font-medium">Leave call</span>
                <span className="block text-[11px] text-ink-4">
                  Clients stay connected — you can rejoin
                </span>
              </span>
            </button>
            <button
              type="button"
              onClick={onEndMeeting}
              disabled={isEndingMeeting}
              className="w-full text-left px-3 py-2.5 rounded-md hover:bg-paper-2 flex items-start gap-2.5 disabled:opacity-60"
            >
              {isEndingMeeting ? (
                <Loader2 size={14} strokeWidth={2} className="mt-0.5 shrink-0 animate-spin text-rust" />
              ) : (
                <Users size={14} strokeWidth={2} className="mt-0.5 shrink-0 text-rust" />
              )}
              <span>
                <span className="block text-[12.5px] font-medium text-rust">
                  End meeting for everyone
                </span>
                <span className="block text-[11px] text-ink-4">
                  Disconnects all participants and ends the session
                </span>
              </span>
            </button>
          </div>
        </>
      )}
    </div>
  );
}

function RecordingNoticeAudio({
  noticeUrl,
  onComplete,
  onError,
}: {
  noticeUrl: string;
  onComplete: () => void;
  onError: () => void;
}) {
  const audioRef = useRef<HTMLAudioElement>(null);
  const [isPlaying, setIsPlaying] = useState(false);
  const [progress, setProgress] = useState(0);

  useEffect(() => {
    const audio = audioRef.current;
    if (!audio) return;

    const handleEnded = () => {
      setIsPlaying(false);
      onComplete();
    };

    const handleError = () => {
      onError();
    };

    const handleTimeUpdate = () => {
      if (audio.duration) {
        setProgress((audio.currentTime / audio.duration) * 100);
      }
    };

    const handleCanPlay = () => {
      audio.play().catch(() => {
        // Autoplay blocked -- show manual play button
        setIsPlaying(false);
      });
      setIsPlaying(true);
    };

    audio.addEventListener("ended", handleEnded);
    audio.addEventListener("error", handleError);
    audio.addEventListener("timeupdate", handleTimeUpdate);
    audio.addEventListener("canplaythrough", handleCanPlay);

    // If the audio doesn't load within 5s, fall back to text notice
    const timeout = setTimeout(() => {
      if (!isPlaying) {
        onError();
      }
    }, 5000);

    return () => {
      audio.removeEventListener("ended", handleEnded);
      audio.removeEventListener("error", handleError);
      audio.removeEventListener("timeupdate", handleTimeUpdate);
      audio.removeEventListener("canplaythrough", handleCanPlay);
      clearTimeout(timeout);
    };
  }, [onComplete, onError, isPlaying]);

  if (!noticeUrl) {
    // No notice URL configured -- show text fallback immediately
    // But still require acknowledgment per edge case #7
    return (
      <div className="space-y-4">
        <div className="bg-spark-s rounded-md p-4 text-[13px] text-spark text-left">
          <p className="font-medium mb-1">Recording notice</p>
          <p className="text-[12px]">
            This session may be recorded for quality assurance purposes. By joining, you
            acknowledge and consent to the recording policy.
          </p>
        </div>
        <button
          type="button"
          onClick={onComplete}
          className="px-5 py-2.5 rounded-md bg-tide text-paper-hi text-[13px] font-medium hover:bg-tide-d"
        >
          I understand, join session
        </button>
      </div>
    );
  }

  return (
    <div className="space-y-4">
      <audio ref={audioRef} src={noticeUrl} preload="auto" />
      <div className="w-full bg-canvas rounded-full h-2 overflow-hidden">
        <div
          className="h-full bg-tide transition-all duration-200"
          style={{ width: `${progress}%` }}
        />
      </div>
      <p className="text-[12px] text-ink-4">
        {isPlaying ? "Playing recording notice..." : "Preparing audio..."}
      </p>
    </div>
  );
}

// Live camera/mic check rendered inside the lobby preview card. Acquires a
// throwaway getUserMedia stream — separate from the tracks connectToRoom
// creates — and shows a self-view PiP plus a mic level meter driven by
// WebAudio. A dead mic otherwise only surfaces after the session as an
// empty transcript.
function LobbyDevicePreview({ providerInitials }: { providerInitials: string }) {
  const videoRef = useRef<HTMLVideoElement | null>(null);
  const meterRef = useRef<HTMLDivElement | null>(null);
  const [cameraOn, setCameraOn] = useState(false);
  const [micOn, setMicOn] = useState(false);

  useEffect(() => {
    let stream: MediaStream | null = null;
    let audioCtx: AudioContext | null = null;
    let raf = 0;
    let cancelled = false;

    (async () => {
      try {
        try {
          stream = await navigator.mediaDevices.getUserMedia({
            video: true,
            audio: true,
          });
        } catch {
          // Camera denied/absent — mic-only still lets them test audio.
          stream = await navigator.mediaDevices.getUserMedia({ audio: true });
        }
      } catch {
        return; // both denied — fall back to the initials placeholder
      }
      if (cancelled) {
        stream.getTracks().forEach((t) => t.stop());
        return;
      }
      const hasVideo = stream.getVideoTracks().length > 0;
      setCameraOn(hasVideo);
      if (hasVideo && videoRef.current) {
        videoRef.current.srcObject = stream;
        videoRef.current.play().catch(() => {});
      }
      if (stream.getAudioTracks().length > 0) {
        setMicOn(true);
        audioCtx = new AudioContext();
        const source = audioCtx.createMediaStreamSource(stream);
        const analyser = audioCtx.createAnalyser();
        analyser.fftSize = 512;
        source.connect(analyser);
        const data = new Uint8Array(analyser.frequencyBinCount);
        const tick = () => {
          analyser.getByteFrequencyData(data);
          let sum = 0;
          for (let i = 0; i < data.length; i++) sum += data[i];
          const level = Math.min(100, (sum / data.length) * 1.8);
          // Write the meter width directly — a React state update per
          // animation frame would re-render the whole lobby at 60fps.
          if (meterRef.current) meterRef.current.style.width = `${level}%`;
          raf = requestAnimationFrame(tick);
        };
        tick();
      }
    })();

    return () => {
      cancelled = true;
      cancelAnimationFrame(raf);
      if (audioCtx) audioCtx.close().catch(() => {});
      if (stream) stream.getTracks().forEach((t) => t.stop());
      if (videoRef.current) videoRef.current.srcObject = null;
    };
  }, []);

  return (
    <>
      {/* Self-view PiP */}
      <div className="absolute bottom-4 right-4 w-44 aspect-video rounded-md overflow-hidden ring-2 ring-paper-hi/20 bg-gradient-to-br from-ink-3 to-ink-2 flex items-center justify-center">
        {cameraOn ? (
          <video
            ref={videoRef}
            autoPlay
            playsInline
            muted
            className="w-full h-full object-cover"
            style={{ transform: "scaleX(-1)" }}
          />
        ) : (
          <div className="w-8 h-8 rounded-full bg-canvas-2 text-ink flex items-center justify-center text-[10px] font-semibold font-display">
            {providerInitials}
          </div>
        )}
      </div>
      {/* Mic level meter */}
      <div className="absolute bottom-4 left-4 flex items-center gap-2 px-3 py-2 rounded-md bg-ink/70 backdrop-blur-sm">
        {micOn ? (
          <Mic size={13} strokeWidth={2} className="text-sage shrink-0" />
        ) : (
          <MicOff size={13} strokeWidth={2} className="text-rust shrink-0" />
        )}
        <div className="w-24 h-1.5 rounded-full bg-paper-hi/20 overflow-hidden">
          <div
            ref={meterRef}
            className="h-full rounded-full bg-sage"
            style={{ width: 0, transition: "width 80ms linear" }}
          />
        </div>
        <span className="text-[10px] text-paper-hi/70">
          {micOn ? "Say something" : "Mic unavailable"}
        </span>
      </div>
    </>
  );
}

function LobbyView({
  modality,
  setModality,
  booking,
  customerName,
  customerInitials,
  providerInitials,
  isJoining,
  isCallingMe,
  customerHasJoined,
  onJoinVideo,
  onCallMe,
  tenantPhoneNumber,
  providerCode,
}: {
  modality: Modality;
  setModality: (m: Modality) => void;
  booking: { method: string } | null;
  customerName: string;
  customerInitials: string;
  providerInitials: string;
  isJoining: boolean;
  isCallingMe: boolean;
  onJoinVideo: () => void;
  onCallMe: () => void;
  tenantPhoneNumber: string | null;
  providerCode: AccessCode | undefined;
  customerHasJoined: boolean;
}) {
  const bookedMethod = booking?.method ?? "video";

  return (
    <div className="p-6 space-y-5">
      {/* Preview card */}
      <div
        className="relative bg-ink overflow-hidden rounded-lg shadow-card"
        style={{ aspectRatio: "16 / 9" }}
      >
        <div className="absolute inset-0 bg-gradient-to-br from-tide/20 via-ink-2 to-ink flex items-center justify-center">
          <div className="flex flex-col items-center gap-3">
            <div className="w-20 h-20 rounded-full bg-tide/30 text-paper-hi flex items-center justify-center text-[28px] font-semibold font-display">
              {customerInitials}
            </div>
            <div className="text-paper-hi text-[16px] font-medium">{customerName}</div>
            <div className="text-[12px] text-paper-hi/60">
              {customerHasJoined
                ? "Waiting for you to join"
                : "Hasn't joined yet"}
            </div>
          </div>
        </div>
        {/* Self-preview PiP — live device check while in the lobby.
            Unmounts (and releases devices) once joining starts so Twilio
            can acquire them cleanly. */}
        {!isJoining ? (
          <LobbyDevicePreview providerInitials={providerInitials} />
        ) : (
          <div className="absolute bottom-4 right-4 w-24 h-16 rounded-md overflow-hidden ring-2 ring-paper-hi/20 bg-gradient-to-br from-ink-3 to-ink-2 flex items-center justify-center">
            <div className="w-8 h-8 rounded-full bg-canvas-2 text-ink flex items-center justify-center text-[10px] font-semibold font-display">
              {providerInitials}
            </div>
          </div>
        )}
      </div>

      {/* Modality selector */}
      <div className="bg-paper rounded-lg shadow-card p-5">
        <div className="text-[10.5px] uppercase tracking-wider font-semibold text-ink-4 mb-3">
          How would you like to join?
        </div>

        <div className="grid grid-cols-2 gap-3 mb-4">
          <button
            type="button"
            onClick={() => setModality("video")}
            className={`px-4 py-3 rounded-lg border-2 text-left transition ${
              modality === "video"
                ? "border-tide bg-tide-ss/50"
                : "border-ink/10 hover:border-ink/20"
            }`}
          >
            <VideoIcon
              size={18}
              className={modality === "video" ? "text-tide mb-1.5" : "text-ink-4 mb-1.5"}
            />
            <div className="text-[13px] font-medium">Video</div>
            <div className="text-[11px] text-ink-4">Join by browser</div>
            {bookedMethod === "video" && (
              <div className="mt-1.5">
                <Badge variant="spark">Booked method</Badge>
              </div>
            )}
          </button>
          <button
            type="button"
            onClick={() => setModality("phone")}
            className={`px-4 py-3 rounded-lg border-2 text-left transition ${
              modality === "phone"
                ? "border-tide bg-tide-ss/50"
                : "border-ink/10 hover:border-ink/20"
            }`}
          >
            <Phone
              size={18}
              className={modality === "phone" ? "text-tide mb-1.5" : "text-ink-4 mb-1.5"}
            />
            <div className="text-[13px] font-medium">Phone</div>
            <div className="text-[11px] text-ink-4">Dial in or get called</div>
            {bookedMethod === "phone" && (
              <div className="mt-1.5">
                <Badge variant="spark">Booked method</Badge>
              </div>
            )}
          </button>
        </div>

        {/* Video join */}
        {modality === "video" && (
          <button
            type="button"
            onClick={onJoinVideo}
            disabled={isJoining}
            className="w-full px-4 py-3 rounded-md bg-tide text-paper-hi text-[14px] font-medium hover:bg-tide-d flex items-center justify-center gap-2 disabled:opacity-60 disabled:cursor-not-allowed"
          >
            {isJoining ? (
              <>
                <Loader2 size={16} className="animate-spin" />
                Connecting...
              </>
            ) : (
              <>
                <VideoIcon size={16} />
                Join session
              </>
            )}
          </button>
        )}

        {/* Phone join -- three options */}
        {modality === "phone" && (
          <div className="space-y-3">
            {/* 1. Call Me */}
            <button
              type="button"
              onClick={onCallMe}
              disabled={isCallingMe}
              className="w-full px-4 py-3 rounded-md bg-tide text-paper-hi text-[14px] font-medium hover:bg-tide-d flex items-center justify-center gap-2 disabled:opacity-60 disabled:cursor-not-allowed"
            >
              {isCallingMe ? (
                <>
                  <Loader2 size={16} className="animate-spin" />
                  Calling your phone...
                </>
              ) : (
                <>
                  <PhoneCall size={16} />
                  Call me
                </>
              )}
            </button>
            <div className="text-[11px] text-ink-4 text-center">
              We will call the phone number on your account
            </div>

            <div className="relative my-3">
              <div className="absolute inset-0 flex items-center">
                <div className="w-full border-t border-ink/10" />
              </div>
              <div className="relative flex justify-center">
                <span className="bg-paper px-3 text-[11px] text-ink-4">or</span>
              </div>
            </div>

            {/* 2. Click to call */}
            {providerCode && tenantPhoneNumber && (
              <a
                href={`tel:${tenantPhoneNumber.replace(/\D/g, "")},,,${providerCode.code}#`}
                className="w-full px-4 py-3 rounded-md hair border bg-paper-hi text-[13px] text-ink-2 font-medium hover:bg-paper-2 flex items-center justify-center gap-2"
              >
                <PhoneOutgoing size={14} />
                Dial from this device
              </a>
            )}

            {/* 3. Manual dial-in */}
            {tenantPhoneNumber ? (
              <div className="bg-canvas rounded-md p-4 text-[12px]">
                <div className="text-[10.5px] uppercase tracking-wider font-semibold text-ink-4 mb-2">
                  Or call manually
                </div>
                <div className="space-y-1.5">
                  <div className="flex items-center justify-between">
                    <span className="text-ink-4">Number</span>
                    <span className="font-mono font-medium num">{tenantPhoneNumber}</span>
                  </div>
                  {providerCode && (
                    <div className="flex items-center justify-between">
                      <span className="text-ink-4">Your code</span>
                      <span className="font-mono font-medium num">{providerCode.code}</span>
                    </div>
                  )}
                  <div className="text-[11px] text-ink-4 leading-snug">
                    Share with clients who need to dial in.
                  </div>
                </div>
              </div>
            ) : (
              <div className="bg-canvas rounded-md p-4 text-[12px] text-ink-4 text-center">
                Phone dial-in is not available for this session.
              </div>
            )}
          </div>
        )}
      </div>
    </div>
  );
}

function ParticipantTile({
  identity,
  track,
  customerByIdentity,
  isSpotlight,
}: {
  identity: string;
  track: TwilioTrack | undefined;
  customerByIdentity: Map<string, CustomerRow>;
  isSpotlight?: boolean;
}) {
  const customer = customerByIdentity.get(identity);
  const label = identityToLabel(identity, customerByIdentity);
  const initials = customer
    ? clientInitials(customer)
    : identity.startsWith("customer-")
      ? "C"
      : "?";

  return (
    <div className="relative w-full h-full overflow-hidden rounded-md bg-ink-2">
      {track ? (
        <VideoTile track={track} className="absolute inset-0 w-full h-full" />
      ) : (
        <div className="absolute inset-0 flex flex-col items-center justify-center gap-2 bg-gradient-to-br from-ink-3 to-ink-2">
          <div className="w-16 h-16 rounded-full bg-tide text-paper-hi flex items-center justify-center text-[22px] font-semibold font-display">
            {initials}
          </div>
          <div className="text-[11px] text-paper-hi/60">Camera off</div>
        </div>
      )}
      <div
        className={`absolute top-2 left-2 backdrop-blur px-2 py-1 rounded text-[10px] text-paper-hi font-medium ${
          isSpotlight ? "bg-tide/80" : "bg-ink/80"
        }`}
      >
        {label}
        {isSpotlight && (
          <span className="ml-1 text-[9px] bg-tide rounded-full px-1">Speaking</span>
        )}
      </div>
    </div>
  );
}

function VideoView({
  remoteIdentities,
  remoteVideoTracks,
  customerByIdentity,
  localVideoTrack,
  dominantSpeaker,
  customerName,
  customerInitials,
  providerInitials,
  isCameraOff,
  isMuted,
  remoteJoinMethod,
}: {
  remoteIdentities: Set<string>;
  remoteVideoTracks: Map<string, TwilioTrack>;
  customerByIdentity: Map<string, CustomerRow>;
  dominantSpeaker?: string;
  localVideoTrack: TwilioTrack | null;
  customerName: string;
  customerInitials: string;
  providerInitials: string;
  isCameraOff: boolean;
  isMuted: boolean;
  remoteJoinMethod?: "phone" | "video" | null;
}) {
  // Roster-driven: render a tile for every connected remote, whether or not
  // they have a video track yet. A track that arrives later just swaps the
  // placeholder out.
  const identities = Array.from(remoteIdentities);
  const remoteCount = identities.length;

  // Spotlight the dominant speaker only when the grid is busy enough that
  // shrinking everyone equally would hurt visibility (4+ remotes).
  const showSpotlight = !!dominantSpeaker && remoteCount >= 4
    && identities.includes(dominantSpeaker);
  const spotlightId = showSpotlight ? dominantSpeaker! : null;
  const otherIds = spotlightId
    ? identities.filter((id) => id !== spotlightId)
    : identities;

  // Layout class for the non-spotlight grid. `auto-rows-fr` keeps tiles
  // square-ish when wrapped.
  let gridClass = "";
  if (remoteCount === 1) {
    gridClass = "grid grid-cols-1 w-full h-full";
  } else if (remoteCount === 2) {
    gridClass = "grid grid-cols-2 gap-2 p-2 w-full h-full";
  } else if (remoteCount === 3) {
    gridClass = "grid grid-cols-3 gap-2 p-2 w-full h-full";
  } else {
    // 4+ — use a 2-column grid; the spotlight tile (if any) sits to the side
    gridClass = "grid grid-cols-2 gap-2 p-2 w-full h-full auto-rows-fr";
  }

  return (
    <div
      className="relative bg-ink overflow-hidden flex-[2] min-h-[320px] m-4 rounded-lg"
    >
      {/* Remote video area */}
      <div className="absolute inset-0 bg-gradient-to-br from-tide/30 via-ink-2 to-ink flex items-center justify-center">
        {remoteCount === 0 ? (
          <div className="flex flex-col items-center gap-3 relative">
            <div className="w-28 h-28 rounded-full bg-tide text-paper-hi flex items-center justify-center text-[40px] font-semibold font-display shadow-2xl">
              {customerInitials}
            </div>
            <div>
              <div className="text-paper-hi text-[16px] font-medium text-center">{customerName}</div>
              {remoteJoinMethod === "phone" ? (
                <div className="flex items-center justify-center gap-1.5 mt-1">
                  <Phone size={11} className="text-paper-hi/60" />
                  <span className="text-[11.5px] text-paper-hi/60">
                    Joined via phone, no video
                  </span>
                </div>
              ) : (
                <div className="text-[11.5px] text-paper-hi/60 mt-1 text-center">
                  Waiting for video...
                </div>
              )}
            </div>
          </div>
        ) : spotlightId ? (
          // 4+ with spotlight: big tile on the left, others stacked on the right
          <div className="grid grid-cols-3 gap-2 p-2 w-full h-full">
            <div className="col-span-2 row-span-1 h-full">
              <ParticipantTile
                identity={spotlightId}
                track={remoteVideoTracks.get(spotlightId)}
                customerByIdentity={customerByIdentity}
                isSpotlight
              />
            </div>
            <div className="col-span-1 grid grid-rows-3 gap-2 h-full">
              {otherIds.slice(0, 3).map((identity) => (
                <ParticipantTile
                  key={identity}
                  identity={identity}
                  track={remoteVideoTracks.get(identity)}
                  customerByIdentity={customerByIdentity}
                />
              ))}
            </div>
          </div>
        ) : (
          <div className={gridClass}>
            {identities.map((identity) => (
              <ParticipantTile
                key={identity}
                identity={identity}
                track={remoteVideoTracks.get(identity)}
                customerByIdentity={customerByIdentity}
              />
            ))}
          </div>
        )}
      </div>

      {/* Mic status indicator (top-left) */}
      {isMuted && (
        <div className="absolute top-3 left-3 bg-rust/90 backdrop-blur px-2.5 py-1.5 rounded-md flex items-center gap-1.5">
          <MicOff size={10} className="text-paper-hi" />
          <span className="text-[10.5px] text-paper-hi font-semibold uppercase tracking-wider">
            Muted
          </span>
        </div>
      )}

      {/* Self-view PiP (bottom-right) */}
      <div className="absolute bottom-4 right-4 w-36 h-[100px] rounded-md overflow-hidden shadow-2xl ring-2 ring-paper-hi/20 bg-gradient-to-br from-ink-3 to-ink-2 flex items-center justify-center">
        {localVideoTrack && (
          <VideoTile
            track={localVideoTrack}
            mirror
            muted
            className="absolute inset-0"
          />
        )}
        {(isCameraOff || !localVideoTrack) && (
          <div className="relative z-10 w-12 h-12 rounded-full bg-canvas-2 text-ink flex items-center justify-center text-[14px] font-semibold font-display">
            {providerInitials}
          </div>
        )}
      </div>
    </div>
  );
}


function PhoneConnectedView({
  customerName,
  customerInitials,
  providerInitials,
  onSwitchToVideo,
  timerPaused,
}: {
  customerName: string;
  customerInitials: string;
  providerInitials: string;
  onSwitchToVideo: () => void;
  timerPaused: boolean;
}) {
  return (
    <div className={`bg-ink rounded-lg p-8 mx-6 mt-6 ${timerPaused ? "opacity-80" : ""}`}>
      <div className="flex items-center justify-center gap-8">
        {/* Provider */}
        <div className="flex flex-col items-center gap-2">
          <div className="w-16 h-16 rounded-full bg-canvas-2 text-ink flex items-center justify-center text-[18px] font-semibold font-display">
            {providerInitials}
          </div>
          <div className="text-[12px] text-paper-hi/60">You (phone)</div>
        </div>

        {/* Connection indicator */}
        <div className="flex items-center gap-2">
          <div className="w-2 h-2 rounded-full bg-sage pulse-soft" />
          <div className="w-8 h-px bg-paper-hi/30" />
          <Phone size={16} className="text-sage" />
          <div className="w-8 h-px bg-paper-hi/30" />
          <div className="w-2 h-2 rounded-full bg-sage pulse-soft" />
        </div>

        {/* Customer */}
        <div className="flex flex-col items-center gap-2">
          <div className="w-16 h-16 rounded-full bg-tide text-paper-hi flex items-center justify-center text-[18px] font-semibold font-display">
            {customerInitials}
          </div>
          <div className="text-[12px] text-paper-hi/60">{customerName}</div>
        </div>
      </div>

      <div className="text-center mt-6">
        <div className="text-[10.5px] uppercase tracking-wider text-paper-hi/50 font-medium">
          {timerPaused ? "Waiting for customer" : "Connected by phone"}
        </div>
        {timerPaused && (
          <div className="text-[11px] text-tide mt-1 font-medium">
            Paused until customer rejoins
          </div>
        )}
      </div>

      <div className="flex justify-center mt-6">
        <button
          type="button"
          onClick={onSwitchToVideo}
          className="px-4 py-2 rounded-md hair border border-paper-hi/20 text-[12.5px] text-paper-hi/80 hover:text-paper-hi hover:bg-paper-hi/10 font-medium flex items-center gap-1.5 transition"
        >
          <VideoIcon size={14} />
          Switch to video
        </button>
      </div>
    </div>
  );
}
